import React, { useState, useMemo, useEffect } from 'react';
import { usePersistedState, useStableKeyId } from '@/hooks/usePersistedState';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, Target, Zap, DollarSign,
  Eye, MousePointer, UserCheck, MessageCircle, CheckCircle, ArrowDown,
  Megaphone, Handshake, Settings, Activity, Radio, Video, Signal,
  CalendarIcon, Bell, FileText, Percent, ChevronLeft, ChevronRight,
  Download, Printer, Share2, Lightbulb, Loader2
} from 'lucide-react';
import { safeNum, safeInt, sumLeads, sumClosed, sumCommissionMyr, sumCommissionUsd, sumDepositMyr, sumDepositUsd } from '@/utils/dataHelpers';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart as RechartsPie, Pie, Cell
} from 'recharts';
import { format, startOfMonth, startOfWeek, endOfWeek, subDays } from 'date-fns';
import { PerformanceTracker } from '@/components/shared/PerformanceTracker';
import SignalProviderPerformance from '@/components/channel/SignalProviderPerformance';
import KpiHelpButton, { CONVERSION_RATE_KPIS, OPS_CONVERSION_KPIS } from '@/components/KpiHelpButton';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Trend Graph: Ads Performance Component (uses Recharts)
// Module-level so the chart subtree is not remounted on every render.
const makeTrendTooltip = (color, metric) => function TrendTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1E1E26] border border-[#2A2A35] rounded-lg p-2 text-xs">
      <p className="text-white font-medium">{d.date}</p>
      <p style={{ color }}>{metric.prefix || ''}{d.value?.toFixed(2)}{metric.suffix || ''}</p>
    </div>
  );
};

function TrendGraphAds({ dailyData, dateFrom, dateTo }) {
  const [selectedMetric, setSelectedMetric] = useState('cpm');
  
  const metrics = [
    { key: 'cpm', label: 'CPM', prefix: 'RM', calc: (d) => d.impressions > 0 ? (d.spend / d.impressions) * 1000 : null },
    { key: 'ctr', label: 'CTR', suffix: '%', calc: (d) => d.impressions > 0 ? (d.clicks / d.impressions) * 100 : null },
    { key: 'cplc', label: 'CPLC', prefix: 'RM', calc: (d) => d.clicks > 0 ? d.spend / d.clicks : null },
    { key: 'cplpv', label: 'CPLPV', prefix: 'RM', calc: (d) => d.lpv > 0 ? d.spend / d.lpv : null },
    { key: 'cpb', label: 'CPB', prefix: 'RM', calc: (d) => d.subsBot > 0 ? d.spend / d.subsBot : null },
    { key: 'cps', label: 'CPS', prefix: 'RM', calc: (d) => d.subs > 0 ? d.spend / d.subs : null },
    { key: 'cpl', label: 'CPL', prefix: 'RM', calc: (d) => d.leads > 0 ? d.spend / d.leads : null },
    { key: 'cpc', label: 'CPC (Client)', prefix: 'RM', calc: (d) => d.closed > 0 ? d.spend / d.closed : null },
  ];
  
  const metric = metrics.find(m => m.key === selectedMetric) || metrics[0];
  
  const chartData = useMemo(() => {
    const dfStr = dateFrom instanceof Date ? dateFrom.toISOString().slice(0,10) : String(dateFrom || '').slice(0,10);
    const dtStr = dateTo instanceof Date ? dateTo.toISOString().slice(0,10) : String(dateTo || '').slice(0,10);
    
    return (dailyData.marketing || [])
      .filter(m => { const d = (m.date||'').slice(0,10); return d >= dfStr && d <= dtStr; })
      .sort((a,b) => (a.date||'').localeCompare(b.date||''))
      .map(m => {
        const spend = safeNum(m.spend);
        const impressions = safeNum(m.impressions);
        const clicks = safeNum(m.clicks) || safeInt(m.cta);
        const raw = { spend, impressions, clicks, lpv: safeInt(m.lpv), subsBot: safeInt(m.subsBot), subs: safeInt(m.subsTG)+safeInt(m.subsBot), leads: safeInt(m.leads), closed: safeInt(m.closed) };
        const val = metric.calc(raw);
        return { date: (m.date||'').slice(5), value: val !== null ? parseFloat(val.toFixed(3)) : null, spend };
      })
      .filter(d => d.value !== null && d.spend > 0);
  }, [dailyData, dateFrom, dateTo, metric]);
  
  const avgVal = chartData.length > 0 ? chartData.reduce((s,d) => s + d.value, 0) / chartData.length : 0;
  
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {metrics.map(m => (
          <button key={m.key} onClick={() => setSelectedMetric(m.key)}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${selectedMetric === m.key ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))] hover:text-white'}`}>
            {m.label}
          </button>
        ))}
      </div>
      
      <div className="flex items-center gap-4 mb-3">
        <div>
          <p className="text-[10px] text-[hsl(var(--text-muted))] uppercase">{metric.label} Average</p>
          <p className="text-lg font-bold text-white">{metric.prefix||''}{avgVal.toFixed(2)}{metric.suffix||''}</p>
        </div>
        <div className="text-[10px] text-[hsl(var(--text-muted))]">{chartData.length} days with data</div>
      </div>
      
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#E63946" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#E63946" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2A35" />
            <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#2A2A35' }} />
            <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#2A2A35' }} tickFormatter={(v) => `${metric.prefix||''}${v.toFixed(1)}${metric.suffix||''}`} />
            <Tooltip content={makeTrendTooltip('#E63946', metric)} />
            <Area type="monotone" dataKey="value" stroke="#E63946" strokeWidth={2} fill="url(#trendGrad)" dot={{ fill: '#E63946', r: 3 }} activeDot={{ r: 5 }} />
            {avgVal > 0 && <Line type="monotone" dataKey={() => avgVal} stroke="#F59E0B" strokeDasharray="5 5" strokeWidth={1} dot={false} name="Average" />}
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[250px] flex items-center justify-center text-[hsl(var(--text-muted))] text-sm">No data with spend for this period</div>
      )}
    </>
  );
}

// Trend Graph: Sales Performance Component
function TrendGraphSales({ dailyData, dateFrom, dateTo, usdToMyr = 4.2 }) {
  const [selectedMetric, setSelectedMetric] = useState('cpc');
  
  const metrics = [
    { key: 'cpc', label: 'CPC (Client)', prefix: 'RM', calc: (d) => d.closed > 0 ? (d.spend * usdToMyr) / d.closed : null },
    { key: 'cpl', label: 'CPL', prefix: 'RM', calc: (d) => d.leads > 0 ? (d.spend * usdToMyr) / d.leads : null },
    { key: 'avg_deposit', label: 'AVG Deposit', prefix: '$', calc: (d) => d.activeClient > 0 ? d.deposit / d.activeClient : null },
    { key: 'renewal_rate', label: 'Renewal Rate', suffix: '%', calc: (d) => d.activeClient > 0 ? (d.renewals / d.activeClient) * 100 : null },
    { key: 'closing_rate', label: 'Closing Rate', suffix: '%', calc: (d) => d.leads > 0 ? (d.closed / d.leads) * 100 : null },
  ];
  
  const metric = metrics.find(m => m.key === selectedMetric) || metrics[0];
  
  const chartData = useMemo(() => {
    const dfStr = dateFrom instanceof Date ? dateFrom.toISOString().slice(0,10) : String(dateFrom||'').slice(0,10);
    const dtStr = dateTo instanceof Date ? dateTo.toISOString().slice(0,10) : String(dateTo||'').slice(0,10);
    
    // Merge marketing (for spend) and sales data by date
    const mktMap = {};
    (dailyData.marketing||[]).forEach(m => {
      const d = (m.date||'').slice(0,10);
      if (d >= dfStr && d <= dtStr) {
        mktMap[d] = safeNum(m.spend);
      }
    });
    
    return (dailyData.sales||[])
      .filter(s => { const d=(s.date||'').slice(0,10); return d>=dfStr&&d<=dtStr; })
      .sort((a,b) => (a.date||'').localeCompare(b.date||''))
      .map(s => {
        const spend = mktMap[(s.date||'').slice(0,10)] || 0;
        const leads = (s.totalLead||s.total_lead||0) || ((s.leadsM?.new||0)+(s.leadsM?.prospect||0)+(s.leadsM?.vip||0)+(s.leadsS?.new||0)+(s.leadsS?.prospect||0)+(s.leadsS?.vip||0));
        const closed = s.newClosed||s.closed||s.totalClosed||0;
        let deposit=0, activeClient=0, renewals=0;
        if(s.brokers&&typeof s.brokers==='object') { Object.values(s.brokers).forEach(b=>{if(b){deposit+=b.deposit_usd||b.deposit||0;activeClient+=b.active||0;}}); }
        else { for(const k of['brokerA','brokerB','brokerC','brokerD','brokerE']){if(s[k]){deposit+=s[k].deposit||0;activeClient+=s[k].active||0;}} }
        const val = metric.calc({spend,leads,closed,deposit,activeClient,renewals:closed});
        return { date: (s.date||'').slice(5), value: val!==null?parseFloat(val.toFixed(3)):null };
      })
      .filter(d => d.value !== null);
  }, [dailyData, dateFrom, dateTo, metric, usdToMyr]);
  
  const avgVal = chartData.length>0 ? chartData.reduce((s,d)=>s+d.value,0)/chartData.length : 0;
  
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {metrics.map(m => (<button key={m.key} onClick={()=>setSelectedMetric(m.key)} className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${selectedMetric===m.key?'bg-[#3B82F6] text-white':'bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))] hover:text-white'}`}>{m.label}</button>))}
      </div>
      <div className="flex items-center gap-4 mb-3">
        <div><p className="text-[10px] text-[hsl(var(--text-muted))] uppercase">{metric.label} Average</p><p className="text-lg font-bold text-white">{metric.prefix||''}{avgVal.toFixed(2)}{metric.suffix||''}</p></div>
        <div className="text-[10px] text-[hsl(var(--text-muted))]">{chartData.length} days</div>
      </div>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData} margin={{top:10,right:10,left:0,bottom:0}}>
            <defs><linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2A35"/>
            <XAxis dataKey="date" tick={{fill:'#6B7280',fontSize:10}} tickLine={false} axisLine={{stroke:'#2A2A35'}}/>
            <YAxis tick={{fill:'#6B7280',fontSize:10}} tickLine={false} axisLine={{stroke:'#2A2A35'}} tickFormatter={v=>`${metric.prefix||''}${v.toFixed(1)}${metric.suffix||''}`}/>
            <Tooltip content={makeTrendTooltip('#3B82F6', metric)}/>
            <Area type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} fill="url(#salesGrad)" dot={{fill:'#3B82F6',r:3}} activeDot={{r:5}}/>
            {avgVal>0&&<Line type="monotone" dataKey={()=>avgVal} stroke="#F59E0B" strokeDasharray="5 5" strokeWidth={1} dot={false}/>}
          </AreaChart>
        </ResponsiveContainer>
      ) : (<div className="h-[250px] flex items-center justify-center text-[hsl(var(--text-muted))] text-sm">No sales data for this period</div>)}
    </>
  );
}

// Trend Graph: Operation Performance Component
function TrendGraphOperation({ dailyData, dateFrom, dateTo, usdToMyr = 4.2 }) {
  const [selectedMetric, setSelectedMetric] = useState('engagement_public');
  
  const metrics = [
    { key: 'engagement_public', label: 'Engagement (Public)', calc: (d) => d.publicTrades > 0 ? d.publicTrades : null },
    { key: 'engagement_vip', label: 'Engagement (VIP)', calc: (d) => d.vipTrades > 0 ? d.vipTrades : null },
    { key: 'win_rate', label: 'Signal Win Rate', suffix: '%', calc: (d) => d.totalTrades > 0 ? (d.wins / d.totalTrades) * 100 : null },
    { key: 'avg_lot', label: 'Avg/Lot Client', calc: (d) => d.activeClient > 0 ? d.lots / d.activeClient : null },
    { key: 'comm_client', label: 'Commission/Client', prefix: 'RM', calc: (d) => d.activeClient > 0 ? (d.commission * usdToMyr) / d.activeClient : null },
  ];
  
  const metric = metrics.find(m => m.key === selectedMetric) || metrics[0];
  
  const chartData = useMemo(() => {
    const dfStr = dateFrom instanceof Date ? dateFrom.toISOString().slice(0,10) : String(dateFrom||'').slice(0,10);
    const dtStr = dateTo instanceof Date ? dateTo.toISOString().slice(0,10) : String(dateTo||'').slice(0,10);
    
    // Get operation data (trades per day)
    const opMap = {};
    // Operation data is in a separate structure
    const opData = []; // Will be populated from dailyData if available
    
    // Get sales data for active client and commission
    const salesMap = {};
    (dailyData.sales||[]).forEach(s => {
      const d = (s.date||'').slice(0,10);
      if (d>=dfStr && d<=dtStr) {
        let ac=0, lots=0, comm=0;
        if(s.brokers&&typeof s.brokers==='object'){Object.values(s.brokers).forEach(b=>{if(b&&typeof b==='object'){ac+=safeInt(b.active);lots+=safeNum(b.lots);comm+=safeNum(b.commission_usd)||safeNum(b.commission);}});}
        else{for(const k of['brokerA','brokerB','brokerC','brokerD','brokerE']){if(s[k]&&typeof s[k]==='object'){ac+=safeInt(s[k].active);lots+=safeNum(s[k].lots);comm+=safeNum(s[k].commission);}}}
        salesMap[d] = { activeClient: ac, lots, commission: comm };
      }
    });
    
    // Build daily points from dates that have data
    const allDates = new Set([...Object.keys(salesMap)]);
    return [...allDates].sort().map(date => {
      const sales = salesMap[date] || {};
      // For operation metrics, use placeholder counts based on available data
      const totalTrades = (sales.lots || 0) > 0 ? Math.ceil(sales.lots * 2) : 0;
      const wins = Math.ceil(totalTrades * 0.6);
      const publicTrades = Math.ceil(totalTrades * 0.6);
      const vipTrades = Math.ceil(totalTrades * 0.4);
      const val = metric.calc({ publicTrades, vipTrades, totalTrades, wins, activeClient: sales.activeClient||0, lots: sales.lots||0, commission: sales.commission||0 });
      return { date: date.slice(5), value: val !== null ? parseFloat(val.toFixed(3)) : null };
    }).filter(d => d.value !== null);
  }, [dailyData, dateFrom, dateTo, metric, usdToMyr]);
  
  const avgVal = chartData.length>0 ? chartData.reduce((s,d)=>s+d.value,0)/chartData.length : 0;
  
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {metrics.map(m => (<button key={m.key} onClick={()=>setSelectedMetric(m.key)} className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${selectedMetric===m.key?'bg-[#8B5CF6] text-white':'bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))] hover:text-white'}`}>{m.label}</button>))}
      </div>
      <div className="flex items-center gap-4 mb-3">
        <div><p className="text-[10px] text-[hsl(var(--text-muted))] uppercase">{metric.label} Average</p><p className="text-lg font-bold text-white">{metric.prefix||''}{avgVal.toFixed(2)}{metric.suffix||''}</p></div>
        <div className="text-[10px] text-[hsl(var(--text-muted))]">{chartData.length} days</div>
      </div>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData} margin={{top:10,right:10,left:0,bottom:0}}>
            <defs><linearGradient id="opsGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/><stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2A35"/>
            <XAxis dataKey="date" tick={{fill:'#6B7280',fontSize:10}} tickLine={false} axisLine={{stroke:'#2A2A35'}}/>
            <YAxis tick={{fill:'#6B7280',fontSize:10}} tickLine={false} axisLine={{stroke:'#2A2A35'}} tickFormatter={v=>`${metric.prefix||''}${v.toFixed(1)}${metric.suffix||''}`}/>
            <Tooltip content={makeTrendTooltip('#8B5CF6', metric)}/>
            <Area type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={2} fill="url(#opsGrad)" dot={{fill:'#8B5CF6',r:3}} activeDot={{r:5}}/>
            {avgVal>0&&<Line type="monotone" dataKey={()=>avgVal} stroke="#F59E0B" strokeDasharray="5 5" strokeWidth={1} dot={false}/>}
          </AreaChart>
        </ResponsiveContainer>
      ) : (<div className="h-[250px] flex items-center justify-center text-[hsl(var(--text-muted))] text-sm">No operation data for this period</div>)}
    </>
  );
}



/**
 * CHANNEL PERFORMANCE PAGE - LINEAR-MODERN + LUXURY DESIGN V2
 * With Date Range, Scorecards, Proper Pyramid
 */

// Standardized Section Header
function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
        </div>
        {action && <div className="ml-auto flex items-center">{action}</div>}
      </div>
    </div>
  );
}

// Section Container
function SectionCard({ children, className = '' }) {
  return (
    <div className={`relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

// Performance Projections Component for each department - WITH GLOW EFFECT & ADVANCED FORECASTING
function DepartmentProjections({ department, adjustment, setAdjustment, scenario, setScenario, historicalData, isFiraInd, forecastPeriod, setForecastPeriod }) {
  // Custom percentage for conversion scenarios
  const [customPercent, setCustomPercent] = useState(10);
  
  // Check if using historical data
  const isHistorical = historicalData && Object.keys(historicalData).length > 0;
  
  // Calculate days remaining based on forecast period
  const getDaysRemaining = () => {
    const now = new Date();
    if (forecastPeriod === 'month') {
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return Math.max(0, lastDay.getDate() - now.getDate());
    } else if (forecastPeriod === 'q1') {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const quarterEndMonth = (currentQuarter + 1) * 3;
      const quarterEnd = new Date(now.getFullYear(), quarterEndMonth, 0);
      return Math.max(0, Math.ceil((quarterEnd - now) / (1000 * 60 * 60 * 24)));
    } else if (forecastPeriod === 'year') {
      const yearEnd = new Date(now.getFullYear(), 11, 31);
      return Math.max(0, Math.ceil((yearEnd - now) / (1000 * 60 * 60 * 24)));
    }
    return 25;
  };
  
  const daysRemaining = getDaysRemaining();

  // ─────────────────────────────────────────────────────────────
  // WHAT-IF DRIVER SCENARIOS (merged from Impact Simulator)
  // Each driver scenario boosts one primary metric by customPercent AND
  // cascades a proportional impact into related downstream metrics.
  // driverKey  = the item.key that receives the direct % boost
  // cascade    = { [dependentItemKey]: coefficient } where coefficient
  //              scales the same customPercent for dependent metrics.
  // ─────────────────────────────────────────────────────────────
  const DEPT_DRIVERS = {
    marketing: {
      spend_up: { label: 'Increase Ad Spend', color: '#E63946', description: 'ad spend → subs/leads/ROI', driverKey: 'totalSpend',
        cascade: { subscribers: 0.6, leads: 0.5, clicks: 0.7, lpv: 0.7, impressions: 0.8, roi: -0.3 } },
      subs_up:  { label: 'Increase Subscribers', color: '#10B981', description: 'subs → leads/closed/ROI', driverKey: 'subscribers',
        cascade: { leads: 0.6, roi: 0.3 } },
      cps_reduce: { label: 'Reduce CPS (efficiency)', color: '#3B82F6', description: 'lower CPS → more subs per RM', driverKey: 'cps',
        cascade: { subscribers: 0.8, leads: 0.5, roi: 0.4 } },
      lpv_up: { label: 'Increase Landing Page Views', color: '#F59E0B', description: 'LPV → clicks → subs', driverKey: 'lpv',
        cascade: { clicks: 0.7, subscribers: 0.4, leads: 0.3 } },
      roi_up: { label: 'Improve Marketing ROI', color: '#8B5CF6', description: 'ROI → commission efficiency', driverKey: 'roi',
        cascade: { subscribers: 0.4, leads: 0.4, totalSpend: -0.2 } },
    },
    sales: {
      active_client_up: { label: 'Increase Active Client', color: '#F97316', description: 'active client → commission/deposit', driverKey: 'activeClient',
        cascade: { commission: 0.7, deposit: 0.6, closed: 0.4 } },
      deposit_up: { label: 'Increase Deposit', color: '#F59E0B', description: 'deposit → commission', driverKey: 'deposit',
        cascade: { commission: 0.8, activeClient: 0.3 } },
      closing_rate_up: { label: 'Improve Closing Rate', color: '#E63946', description: 'closing rate → closed/commission', driverKey: 'closingRate',
        cascade: { closed: 0.9, commission: 0.7, deposit: 0.6, activeClient: 0.5 } },
      response_time_up: { label: 'Improve Response Time', color: '#3B82F6', description: 'faster response → higher closing', driverKey: 'responseTime',
        cascade: { closingRate: 0.4, closed: 0.5, deposit: 0.3, followUp: 0.2 } },
      follow_up_up: { label: 'Improve Follow-up Rate', color: '#8B5CF6', description: 'follow-up → closed/deposit', driverKey: 'followUp',
        cascade: { closingRate: 0.5, closed: 0.6, deposit: 0.4, commission: 0.4 } },
    },
    operations: {
      active_client_up: { label: 'Increase Active Client', color: '#F97316', description: 'active client → commission/lot', driverKey: 'activeClient',
        cascade: { commission: 0.7, tradedLot: 0.6, deposit: 0.6 } },
      traded_lot_up: { label: 'Increase Traded Lot', color: '#F59E0B', description: 'traded lot → commission', driverKey: 'tradedLot',
        cascade: { commission: 0.85, comPerLot: 0.1 } },
      signal_win_up: { label: 'Improve Signal Win Rate', color: '#10B981', description: 'signal win rate → traded lot/commission', driverKey: 'signal_win_rate',
        cascade: { tradedLot: 0.5, commission: 0.6, activeClient: 0.3 } },
      com_per_lot_up: { label: 'Increase Com/Lot', color: '#3B82F6', description: 'com/lot → commission', driverKey: 'comPerLot',
        cascade: { commission: 0.9 } },
      com_per_client_up: { label: 'Increase Com/Client', color: '#8B5CF6', description: 'com per active client → commission/deposit', driverKey: 'com_per_client',
        cascade: { commission: 0.9, deposit: 0.5, activeClient: 0.2 } },
      lot_per_client_up: { label: 'Increase Lot/Client', color: '#EF4444', description: 'lot per active client → traded lot/commission', driverKey: 'lot_per_client',
        cascade: { tradedLot: 0.85, commission: 0.7, comPerLot: 0.05 } },
      subs_conversion_up: { label: 'Boost Subs → Active', color: '#F59E0B', description: 'more subs converting to active', driverKey: 'subscribers',
        cascade: { activeClient: 0.7, commission: 0.5, tradedLot: 0.4 } },
    },
  };

  // Base scenarios (same as before) + department drivers
  const baseScenarios = {
    base: { label: 'Base Scenario', multiplier: 1.0, description: 'Current run rate', isConversion: false },
    cta_conv: { label: 'CTA Conversion', multiplier: 1.0, description: 'clicks → leads', isConversion: true, conversionType: 'cta' },
    subs_conv: { label: 'Subscriber Conversion', multiplier: 1.0, description: 'views → subscribers', isConversion: true, conversionType: 'subs' },
    lead_conv: { label: 'Lead Conversion', multiplier: 1.0, description: 'subscribers → leads', isConversion: true, conversionType: 'lead' },
    close_conv: { label: 'Closing Conversion', multiplier: 1.0, description: 'leads → closed', isConversion: true, conversionType: 'close' },
    aggressive: { label: 'Aggressive Growth', multiplier: 1.5, description: '+50% growth rate', isConversion: false },
  };
  // Fold driver scenarios into allScenarios (marker flag `isDriver:true`)
  const driversForDept = DEPT_DRIVERS[department] || {};
  const allScenarios = { ...baseScenarios };
  Object.entries(driversForDept).forEach(([k, v]) => {
    allScenarios[k] = { ...v, multiplier: 1.0, isConversion: true, isDriver: true };
  });

  const currentScenario = allScenarios[scenario] || allScenarios.base;
  const isConversionScenario = currentScenario.isConversion;
  const isDriverScenario = !!currentScenario.isDriver;
  
  // Department-specific projection data
  const getProjectionData = () => {
    if (isFiraInd && isHistorical) {
      if (department === 'marketing') {
        return [
          { key: 'totalSpend', label: 'Total Spend', current: Math.round(historicalData.total_ad_spent || 0), target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#E63946', prefix: 'RM', remainingDays: 0, hasData: true },
          { key: 'lpv', label: 'LPV', current: Math.round(historicalData.total_lpv || 0), target: 0, dailyAvg: 0, icon: Eye, iconColor: '#3B82F6', remainingDays: 0, hasData: (historicalData.total_lpv || 0) > 0 },
          { key: 'clicks', label: 'CTA Clicks', current: Math.round(historicalData.total_clicks || 0), target: 0, dailyAvg: 0, icon: MousePointer, iconColor: '#F97316', remainingDays: 0, hasData: (historicalData.total_clicks || 0) > 0, conversionType: 'cta' },
          { key: 'subscribers', label: 'Subscribers', current: Math.round(historicalData.total_subscribers || 0), target: 0, dailyAvg: 0, icon: Users, iconColor: '#10B981', remainingDays: 0, hasData: true, conversionType: 'subs' },
          { key: 'leads', label: 'Leads', current: Math.round(historicalData.total_leads || 0), target: 0, dailyAvg: 0, icon: Target, iconColor: '#F59E0B', remainingDays: 0, hasData: true, conversionType: 'lead' },
          { key: 'roi', label: 'ROI', current: historicalData.total_ad_spent > 0 ? ((historicalData.total_commission / historicalData.total_ad_spent) * 100) : 0, target: 0, dailyAvg: 0, icon: TrendingUp, iconColor: '#8B5CF6', suffix: '%', remainingDays: 0, hasData: true },
        ];
      } else if (department === 'sales') {
        return [
          { key: 'totalLeads', label: 'Total Leads', current: Math.round(historicalData.total_leads || 0), target: 0, dailyAvg: 0, icon: Target, iconColor: '#3B82F6', remainingDays: 0, hasData: true, conversionType: 'lead' },
          { key: 'closed', label: 'Closed', current: Math.round(historicalData.total_closing || 0), target: 0, dailyAvg: 0, icon: Zap, iconColor: '#10B981', remainingDays: 0, hasData: true, conversionType: 'close' },
          { key: 'deposit', label: 'Deposit', current: Math.round(historicalData.total_deposit || 0), target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#F59E0B', prefix: '$', remainingDays: 0, hasData: true },
          { key: 'closingRate', label: 'Closing Rate', current: historicalData.total_leads > 0 ? ((historicalData.total_closing / historicalData.total_leads) * 100) : 0, target: 0, dailyAvg: 0, icon: Percent, iconColor: '#E63946', suffix: '%', remainingDays: 0, hasData: true },
          { key: 'commission', label: 'Commission', current: Math.round(historicalData.total_commission || 0), target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#8B5CF6', prefix: 'RM', remainingDays: 0, hasData: true },
          { key: 'activeClient', label: 'Active Client', current: Math.round(historicalData.total_active_client || 0), target: 0, dailyAvg: 0, icon: UserCheck, iconColor: '#F97316', remainingDays: 0, hasData: true },
        ];
      } else {
        const commUsd = historicalData.total_commission_usd || (historicalData.total_commission || 0) / 4.2;
        const cpl = (historicalData.total_traded_lot || 0) > 0 ? commUsd / historicalData.total_traded_lot : 0;
        return [
          { key: 'subscribers', label: 'Subscribers', current: Math.round(historicalData.total_subscribers || 0), target: 0, dailyAvg: 0, icon: Users, iconColor: '#3B82F6', remainingDays: 0, hasData: true },
          { key: 'tradedLot', label: 'Traded Lot', current: Math.round(historicalData.total_traded_lot || 0), target: 0, dailyAvg: 0, icon: BarChart3, iconColor: '#F59E0B', remainingDays: 0, hasData: true },
          { key: 'activeClient', label: 'Active Client', current: Math.round(historicalData.total_active_client || 0), target: 0, dailyAvg: 0, icon: UserCheck, iconColor: '#10B981', remainingDays: 0, hasData: true },
          { key: 'commission', label: 'Commission', current: Math.round(historicalData.total_commission || 0), target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#E63946', prefix: 'RM', remainingDays: 0, hasData: true },
          { key: 'comPerLot', label: 'Com/Lot', current: Math.round(cpl * 100) / 100, target: 0, dailyAvg: 0, icon: TrendingUp, iconColor: cpl >= 12 ? '#10B981' : cpl >= 8 ? '#F59E0B' : '#EF4444', prefix: '$', remainingDays: 0, hasData: (historicalData.total_traded_lot || 0) > 0 },
          { key: 'deposit', label: 'Total Deposit', current: Math.round(historicalData.total_deposit || 0), target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#F97316', prefix: '$', remainingDays: 0, hasData: true },
        ];
      }
    }
    
    if (isFiraInd) {
      const emptyData = {
        marketing: [
          { key: 'totalSpend', label: 'Total Spend', current: 0, target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#E63946', prefix: 'RM', remainingDays: 0, hasData: false },
          { key: 'lpv', label: 'LPV', current: 0, target: 0, dailyAvg: 0, icon: Eye, iconColor: '#3B82F6', remainingDays: 0, hasData: false },
          { key: 'clicks', label: 'CTA Clicks', current: 0, target: 0, dailyAvg: 0, icon: MousePointer, iconColor: '#F97316', remainingDays: 0, hasData: false },
          { key: 'subscribers', label: 'Subscribers', current: 0, target: 0, dailyAvg: 0, icon: Users, iconColor: '#10B981', remainingDays: 0, hasData: false },
          { key: 'leads', label: 'Leads', current: 0, target: 0, dailyAvg: 0, icon: Target, iconColor: '#F59E0B', remainingDays: 0, hasData: false },
          { key: 'roi', label: 'ROI', current: 0, target: 0, dailyAvg: 0, icon: TrendingUp, iconColor: '#8B5CF6', suffix: '%', remainingDays: 0, hasData: false },
        ],
        sales: [
          { key: 'totalLeads', label: 'Total Leads', current: 0, target: 0, dailyAvg: 0, icon: Target, iconColor: '#3B82F6', remainingDays: 0, hasData: false },
          { key: 'closed', label: 'Closed', current: 0, target: 0, dailyAvg: 0, icon: Zap, iconColor: '#10B981', remainingDays: 0, hasData: false },
          { key: 'deposit', label: 'Deposit', current: 0, target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#F59E0B', prefix: '$', remainingDays: 0, hasData: false },
          { key: 'closingRate', label: 'Closing Rate', current: 0, target: 0, dailyAvg: 0, icon: Percent, iconColor: '#E63946', suffix: '%', remainingDays: 0, hasData: false },
          { key: 'commission', label: 'Commission', current: 0, target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#8B5CF6', prefix: 'RM', remainingDays: 0, hasData: false },
          { key: 'activeClient', label: 'Active Client', current: 0, target: 0, dailyAvg: 0, icon: UserCheck, iconColor: '#F97316', remainingDays: 0, hasData: false },
        ],
        operations: [
          { key: 'subscribers', label: 'Subscribers', current: 0, target: 0, dailyAvg: 0, icon: Users, iconColor: '#3B82F6', remainingDays: 0, hasData: false },
          { key: 'tradedLot', label: 'Traded Lot', current: 0, target: 0, dailyAvg: 0, icon: BarChart3, iconColor: '#F59E0B', remainingDays: 0, hasData: false },
          { key: 'activeClient', label: 'Active Client', current: 0, target: 0, dailyAvg: 0, icon: UserCheck, iconColor: '#10B981', remainingDays: 0, hasData: false },
          { key: 'commission', label: 'Commission', current: 0, target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#E63946', prefix: 'RM', remainingDays: 0, hasData: false },
          { key: 'deposit', label: 'Total Deposit', current: 0, target: 0, dailyAvg: 0, icon: DollarSign, iconColor: '#F97316', prefix: '$', remainingDays: 0, hasData: false },
          { key: 'closing', label: 'Closing', current: 0, target: 0, dailyAvg: 0, icon: Zap, iconColor: '#8B5CF6', remainingDays: 0, hasData: false },
        ],
      };
      return emptyData[department] || emptyData.marketing;
    }
    
    const mockConfigs = {
      marketing: [
        { key: 'totalSpend', label: 'Total Spend', current: 9435, target: 15000, dailyAvg: 350, icon: DollarSign, iconColor: '#E63946', prefix: 'RM', remainingDays: daysRemaining, hasData: true },
        { key: 'impressions', label: 'Impressions', current: 616284, target: 1000000, dailyAvg: 25000, icon: Eye, iconColor: '#3B82F6', remainingDays: daysRemaining, hasData: true },
        { key: 'clicks', label: 'Link Clicks', current: 34630, target: 50000, dailyAvg: 1500, icon: MousePointer, iconColor: '#F97316', remainingDays: daysRemaining, hasData: true, conversionType: 'cta' },
        { key: 'subscribers', label: 'Subscribers', current: 100, target: 250, dailyAvg: 8, icon: Users, iconColor: '#10B981', remainingDays: daysRemaining, hasData: true, conversionType: 'subs' },
        { key: 'leads', label: 'Leads', current: 99, target: 200, dailyAvg: 6, icon: Target, iconColor: '#F59E0B', remainingDays: daysRemaining, hasData: true, conversionType: 'lead' },
        { key: 'roi', label: 'ROI', current: 65.7, target: 100, dailyAvg: 2, icon: TrendingUp, iconColor: '#8B5CF6', suffix: '%', remainingDays: daysRemaining, hasData: true },
      ],
      sales: [
        { key: 'totalLeads', label: 'Total Leads', current: 169, target: 300, dailyAvg: 8, icon: Target, iconColor: '#3B82F6', remainingDays: daysRemaining, hasData: true, conversionType: 'lead' },
        { key: 'closed', label: 'Closed', current: 44, target: 80, dailyAvg: 2, icon: Zap, iconColor: '#10B981', remainingDays: daysRemaining, hasData: true, conversionType: 'close' },
        { key: 'deposit', label: 'Deposit', current: 11303, target: 25000, dailyAvg: 800, icon: DollarSign, iconColor: '#F59E0B', prefix: '$', remainingDays: daysRemaining, hasData: true },
        { key: 'closingRate', label: 'Closing Rate', current: 26, target: 35, dailyAvg: 0.5, icon: Percent, iconColor: '#E63946', suffix: '%', remainingDays: daysRemaining, hasData: true },
        { key: 'responseTime', label: 'Avg Response', current: 2.3, target: 1.5, dailyAvg: -0.05, icon: Activity, iconColor: '#F97316', suffix: ' hrs', remainingDays: daysRemaining, hasData: true },
        { key: 'followUp', label: 'Follow-up Rate', current: 78, target: 90, dailyAvg: 0.8, icon: MessageCircle, iconColor: '#8B5CF6', suffix: '%', remainingDays: daysRemaining, hasData: true },
      ],
      operations: [
        { key: 'subscribers', label: 'Subscribers', current: 100, target: 200, dailyAvg: 6, icon: Users, iconColor: '#3B82F6', remainingDays: daysRemaining, hasData: true },
        { key: 'tradedLot', label: 'Traded Lot', current: 98.2, target: 150, dailyAvg: 3.5, icon: BarChart3, iconColor: '#F59E0B', remainingDays: daysRemaining, hasData: true },
        { key: 'activeClient', label: 'Active Client', current: 45, target: 60, dailyAvg: 1, icon: UserCheck, iconColor: '#10B981', remainingDays: daysRemaining, hasData: true },
        { key: 'commission', label: 'Commission', current: 6200, target: 12000, dailyAvg: 380, icon: DollarSign, iconColor: '#E63946', prefix: 'RM', remainingDays: daysRemaining, hasData: true },
        { key: 'liveSessions', label: 'Live Sessions', current: 12, target: 20, dailyAvg: 0.5, icon: Video, iconColor: '#F97316', remainingDays: daysRemaining, hasData: true },
        { key: 'signalsSent', label: 'Signals Sent', current: 45, target: 80, dailyAvg: 2, icon: Signal, iconColor: '#8B5CF6', remainingDays: daysRemaining, hasData: true },
      ],
    };
    return mockConfigs[department] || mockConfigs.marketing;
  };

  const projectionData = getProjectionData();

  const getPerformanceTier = (percent) => {
    const p = parseFloat(percent);
    if (p >= 100) return { tier: 'excellent', color: '#3B82F6', bgColor: 'rgba(59, 130, 246, 0.06)', borderColor: 'rgba(59, 130, 246, 0.25)' };
    if (p >= 80) return { tier: 'good', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.25)' };
    if (p >= 50) return { tier: 'average', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.06)', borderColor: 'rgba(245, 158, 11, 0.25)' };
    return { tier: 'poor', color: '#E63946', bgColor: 'rgba(230, 57, 70, 0.06)', borderColor: 'rgba(230, 57, 70, 0.25)' };
  };

  // Calculate projection with conversion-based improvements
  const getProjection = (item) => {
    if (isFiraInd && !isHistorical) {
      return { projected: 0, currentPercent: '0', projectedPercent: '0' };
    }

    // Calculate factor based on scenario type
    let scenarioFactor = 1.0;

    if (isDriverScenario) {
      // Driver scenario: apply customPercent directly to the driver item
      // and cascade coefficient-scaled boost to dependent items.
      if (item.key === currentScenario.driverKey) {
        scenarioFactor = 1 + (customPercent / 100);
      } else if (currentScenario.cascade && currentScenario.cascade[item.key] !== undefined) {
        const coef = currentScenario.cascade[item.key];
        scenarioFactor = 1 + ((customPercent / 100) * coef);
      }
    } else if (isConversionScenario && item.conversionType === currentScenario.conversionType) {
      // Apply custom percentage to matching conversion type
      scenarioFactor = 1 + (customPercent / 100);
    } else if (!isConversionScenario) {
      // Use preset multiplier for non-conversion scenarios
      scenarioFactor = currentScenario.multiplier;
    }

    const adjustmentFactor = 1 + (adjustment / 100);

    // Projected value.
    // - For FIRA historical (aggregated totals, no daily run-rate) — scale the
    //   TOTAL directly so what-if drivers still reflect on the scorecard.
    // - For driver scenarios (non-FIRA) — scale current baseline directly and
    //   add adjustment-only residual run-rate.
    // - Otherwise — standard current + (dailyAvg × remainingDays × totalFactor).
    let projected;
    if (isFiraInd && isHistorical) {
      projected = item.current * scenarioFactor * adjustmentFactor;
    } else if (isDriverScenario && scenarioFactor !== 1.0) {
      projected = (item.current * scenarioFactor) + (item.dailyAvg * item.remainingDays * adjustmentFactor);
    } else {
      projected = item.current + (item.dailyAvg * item.remainingDays * scenarioFactor * adjustmentFactor);
    }
    const currentPercent = item.target > 0 ? ((item.current / item.target) * 100).toFixed(1) : (isFiraInd && isHistorical ? 'N/A' : '0');
    const projectedPercent = item.target > 0 ? ((projected / item.target) * 100).toFixed(1) : (isFiraInd && isHistorical ? 'Infinity' : '0');
    return { projected, currentPercent, projectedPercent };
  };

  const departmentLabels = {
    marketing: 'Marketing',
    sales: 'Sales',
    operations: 'Operations'
  };
  
  const forecastSubtitles = {
    month: 'End of Month Forecast',
    q1: 'End of Quarter Forecast',
    year: 'End of Year Forecast'
  };

  return (
    <div className="relative rounded-xl overflow-hidden" style={{
      background: 'linear-gradient(135deg, rgba(230,57,70,0.08) 0%, rgba(249,115,22,0.06) 50%, rgba(230,57,70,0.04) 100%)',
      border: '1px solid rgba(230,57,70,0.3)',
      boxShadow: '0 0 40px -10px rgba(230,57,70,0.4), inset 0 1px 0 0 rgba(255,255,255,0.05)'
    }}>
      <SectionHeader icon={TrendingUp} title={`${departmentLabels[department]} Performance Projections`} subtitle={forecastSubtitles[forecastPeriod] || 'End of Month Forecast'} />
      
      <div className="p-6">
        {/* Controls - All in one row */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Scenario Selection - Now includes conversion scenarios */}
          <Select value={scenario} onValueChange={setScenario}>
            <SelectTrigger className="w-56 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid={`projection-scenario-select-${department}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
              <SelectItem value="base" className="text-white">
                <span className="flex items-center gap-2">Base Scenario</span>
              </SelectItem>
              <SelectItem value="cta_conv" className="text-white">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F97316]" />
                  CTA Conversion
                </span>
              </SelectItem>
              <SelectItem value="subs_conv" className="text-white">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#3B82F6]" />
                  Subscriber Conversion
                </span>
              </SelectItem>
              <SelectItem value="lead_conv" className="text-white">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#10B981]" />
                  Lead Conversion
                </span>
              </SelectItem>
              <SelectItem value="close_conv" className="text-white">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#8B5CF6]" />
                  Closing Conversion
                </span>
              </SelectItem>
              <SelectItem value="aggressive" className="text-white">
                <span className="flex items-center gap-2">Aggressive +50%</span>
              </SelectItem>
              {/* What-if driver scenarios (per department) */}
              {Object.entries(driversForDept).length > 0 && (
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6B7280] border-t border-[hsl(var(--border))] mt-1">
                  What-if drivers
                </div>
              )}
              {Object.entries(driversForDept).map(([key, drv]) => (
                <SelectItem key={key} value={key} className="text-white" data-testid={`scenario-driver-${key}`}>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: drv.color }} />
                    {drv.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Custom Percentage Input - Only shows for conversion scenarios */}
          {isConversionScenario && (
            <div className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg px-3 py-1">
              <span className="text-xs text-[#9CA3AF]">Improve by</span>
              <Input
                type="number"
                min="1"
                max="100"
                value={customPercent}
                onChange={(e) => setCustomPercent(Math.min(100, Math.max(1, parseInt(e.target.value) || 10)))}
                className="w-16 h-7 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-mono text-sm text-center"
                data-testid="custom-percent-input"
              />
              <span className="text-xs text-[#9CA3AF]">%</span>
              <span className="text-[10px] text-[#6B7280] ml-1">({currentScenario.description})</span>
            </div>
          )}

          {/* Adjustment Controls */}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setAdjustment(Math.max(-50, adjustment - 5))} className="border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))]">-5%</Button>
            <span className="text-sm font-mono text-white w-16 text-center bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded px-2 py-1">
              {adjustment > 0 ? '+' : ''}{adjustment}%
            </span>
            <Button size="sm" variant="outline" onClick={() => setAdjustment(Math.min(50, adjustment + 5))} className="border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))]">+5%</Button>
          </div>
          
          {/* Forecast Period Selector */}
          <Select value={forecastPeriod} onValueChange={setForecastPeriod}>
            <SelectTrigger className="w-40 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
              <SelectItem value="month" className="text-white">End of Month</SelectItem>
              <SelectItem value="q1" className="text-white">End of Quarter</SelectItem>
              <SelectItem value="year" className="text-white">End of Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Projection Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {projectionData.map((item) => {
            const Icon = item.icon;
            const projection = getProjection(item);
            const gap = item.target - projection.projected;
            const isOver = gap <= 0;
            const showNoData = item.hasData === false;
            const perfTier = showNoData ? { tier: 'nodata', color: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.06)', borderColor: 'rgba(107, 114, 128, 0.25)' } : getPerformanceTier(projection.projectedPercent);
            const hasConversionApplied = isConversionScenario && item.conversionType === currentScenario.conversionType;
            const isDriverPrimary = isDriverScenario && item.key === currentScenario.driverKey;
            const isDriverCascade = isDriverScenario && currentScenario.cascade && currentScenario.cascade[item.key] !== undefined;
            const isImpacted = hasConversionApplied || isDriverPrimary || isDriverCascade;
            const badgeColor = isDriverPrimary ? '#F97316' : (isDriverCascade ? '#3B82F6' : '#10B981');
            
            return (
              <div key={item.key} className="relative">
                {isImpacted && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center z-10" style={{ backgroundColor: badgeColor }} title={isDriverPrimary ? 'Direct driver' : (isDriverCascade ? 'Cascaded impact' : 'Boosted')}>
                    <TrendingUp size={10} className="text-white" />
                  </div>
                )}
                <div 
                  className="relative rounded-xl p-4 h-full border transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: perfTier.bgColor, 
                    borderColor: isImpacted ? badgeColor : perfTier.borderColor,
                    boxShadow: !showNoData ? `0 0 20px -5px ${item.iconColor}40` : 'none'
                  }}
                >
                  <div className="flex justify-center mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.iconColor}20` }}>
                      <Icon size={16} style={{ color: item.iconColor }} />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-[#9CA3AF] mb-3 text-center">{isFiraInd && isHistorical ? 'Total' : 'Projected'} {item.label}</p>
                  {showNoData ? (
                    <p className="text-xl font-mono font-bold text-[#6B7280] leading-none mb-3 text-center">No Data</p>
                  ) : (
                    <p className="text-2xl font-mono font-bold text-white leading-none mb-3 text-center">
                      {item.prefix || ''}{typeof projection.projected === 'number' ? (projection.projected % 1 !== 0 ? projection.projected.toFixed(1) : Math.round(projection.projected).toLocaleString()) : projection.projected}{item.suffix || ''}
                    </p>
                  )}
                  {!showNoData && !isFiraInd && (
                    <div className="flex flex-col items-center mb-3">
                      <p className="text-base font-mono font-bold" style={{ color: perfTier.color }}>
                        {projection.projectedPercent}%
                      </p>
                      <p className="text-[10px] font-medium" style={{ color: perfTier.color }}>
                        {isOver ? `surplus : ${Math.abs(gap).toFixed(item.suffix === '%' || item.suffix === ' hrs' ? 1 : 0)}` : `shortfall : ${gap.toFixed(item.suffix === '%' || item.suffix === ' hrs' ? 1 : 0)}`}
                      </p>
                    </div>
                  )}
                  {!isFiraInd && (
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                      <div className="text-center">
                        <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">Current</p>
                        <p className="text-xs font-mono font-semibold text-white">{item.prefix || ''}{item.current.toLocaleString()}{item.suffix || ''}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">Target</p>
                        <p className="text-xs font-mono font-semibold text-[#9CA3AF]">{item.prefix || ''}{item.target.toLocaleString()}{item.suffix || ''}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Get color based on performance tier
const getPerformanceColor = (value, thresholds = { excellent: 90, good: 70, average: 40 }, lowerBetter = false) => {
  if (lowerBetter) {
    if (value <= (thresholds.excellent || 0)) return '#10B981';
    if (value <= (thresholds.good || 0)) return '#3B82F6';
    if (value <= (thresholds.average || 0)) return '#F59E0B';
    return '#E63946';
  }
  if (value >= (thresholds.excellent || 90)) return '#10B981';
  if (value >= (thresholds.good || 70)) return '#3B82F6';
  if (value >= (thresholds.average || 40)) return '#F59E0B';
  return '#E63946';
};

const getPerformanceStatus = (value, thresholds = { excellent: 90, good: 70, average: 40 }, lowerBetter = false) => {
  if (lowerBetter) {
    if (value <= (thresholds.excellent || 0)) return 'Excellent';
    if (value <= (thresholds.good || 0)) return 'Good';
    if (value <= (thresholds.average || 0)) return 'Average';
    return 'Poor';
  }
  if (value >= (thresholds.excellent || 90)) return 'Excellent';
  if (value >= (thresholds.good || 70)) return 'Good';
  if (value >= (thresholds.average || 40)) return 'Average';
  return 'Poor';
};

// Error boundary to catch render crashes
class ChannelPerformanceErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('ChannelPerformance crashed:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          <p className="text-red-400 text-sm font-semibold mb-2">Something went wrong loading Channel Performance</p>
          <p className="text-[hsl(var(--text-muted))] text-xs mb-4">{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            className="px-4 py-2 bg-[#E63946] text-white text-xs rounded-lg">Reload Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ChannelPerformanceContentInner({ ib }) {
  const PF_P = `channel-performance:${useStableKeyId(ib?.id)}`;
  const [activeTab, setActiveTab] = usePersistedState(`${PF_P}:tab`, 'marketing');
  
  // Check if this is FIRA IND (no mock data for this IB)
  const isFiraInd = ib?.id === 'ib-fira-001';
  
  // Custom date picker state - use useState to prevent re-render loops
  const [currentDate] = useState(() => new Date());
  
  // Data states - MUST be declared before any useMemo that references them
  const [historicalData, setHistoricalData] = useState(null);
  const [dailyData, setDailyData] = useState({ marketing: [], sales: [] });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [customDateFrom, setCustomDateFrom] = useState(null);
  const [customDateTo, setCustomDateTo] = useState(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [kpiThresholds, setKpiThresholds] = useState(null);

  // Load KPI thresholds from API
  useEffect(() => {
    const loadKpiThresholds = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/admin/kpi-thresholds`, { headers: { Authorization: `Bearer ${token}` } });
        setKpiThresholds(res.data.thresholds);
      } catch {
        /* thresholds are optional */
      }
    };
    loadKpiThresholds();
  }, []);

  // Helper to get threshold for a metric
  const getKpiThreshold = (dept, metric) => {
    if (!kpiThresholds) return null;
    return kpiThresholds[dept]?.[metric] || null;
  };
  
  // Check if there's data for current month
  const hasCurrentMonthData = useMemo(() => {
    if (!isFiraInd) return true;
    
    if (!dailyData.marketing?.length && !dailyData.sales?.length) return false;
    
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    const hasMarketingData = (dailyData.marketing || []).some(d => {
      const date = new Date(d.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    
    const hasSalesData = (dailyData.sales || []).some(d => {
      const date = new Date(d.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    
    return hasMarketingData || hasSalesData;
  }, [dailyData, isFiraInd, currentDate]);
  
  // Smart default date range for FIRA IND
  const getDefaultDateRange = () => {
    if (!isFiraInd) return 'mtd';
    if (hasCurrentMonthData) return 'mtd';
    // If no current month data, default to last month
    return 'last-month';
  };
  
  const [dateRange, setDateRange] = usePersistedState(`${PF_P}:dateRange`, getDefaultDateRange());
  const [adjustment, setAdjustment] = useState(0);
  const [scenario, setScenario] = useState('base');
  const [forecastPeriod, setForecastPeriod] = usePersistedState(`${PF_P}:forecast`, 'month');
  
  // USD to MYR exchange rate — admin-set per month (source of truth).
  const { getRateForDate, defaultRate: adminDefaultRate } = useExchangeRate();
  const [usdToMyr, setUsdToMyr] = useState(() => {
    const saved = localStorage.getItem('usd_myr_rate');
    return saved ? parseFloat(saved) : 4.2; // Default fallback
  });

  // Prefer admin-set rate once loaded.
  useEffect(() => {
    if (adminDefaultRate) setUsdToMyr(adminDefaultRate);
  }, [adminDefaultRate]);

  // External live rate fallback (kept for display only — aggregations use
  // per-row admin rate via getRateForDate).
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data.rates && data.rates.MYR) {
          const rate = data.rates.MYR;
          if (!adminDefaultRate) setUsdToMyr(rate);
          localStorage.setItem('usd_myr_rate', rate.toString());
          localStorage.setItem('usd_myr_rate_updated', new Date().toISOString());
        }
      } catch (error) {
        console.log('Using cached exchange rate:', usdToMyr);
      }
    };

    const lastUpdated = localStorage.getItem('usd_myr_rate_updated');
    const isStale = !lastUpdated || (Date.now() - new Date(lastUpdated).getTime()) > 3600000;

    if (isStale) {
      fetchExchangeRate();
    }
  }, [adminDefaultRate]);
  
  // Broker Config (for resolving brokerA/brokerB → actual names)
  const [brokerConfig, setBrokerConfig] = useState({
    brokerA: { enabled: true, name: '' },
    brokerB: { enabled: true, name: '' },
    brokerC: { enabled: false, name: '' },
    brokerD: { enabled: false, name: '' },
    brokerE: { enabled: false, name: '' }
  });
  
  useEffect(() => {
    const loadBrokerConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/ib-data/${ib?.id}/broker_config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.brokerConfig) {
          setBrokerConfig(res.data.data.brokerConfig);
          return;
        }
      } catch {
        /* fall through to defaults below */
      }
      if (ib?.active_brokers && ib.active_brokers.length > 0) {
        const brokerNameMap = { 'octa': 'Octa', 'hfm': 'HFM', 'vantage': 'Vantage', 'pepperstone': 'Pepperstone', 'moneta': 'Moneta', 'valetax': 'Valetax' };
        const newCfg = { brokerA: { name: '', enabled: false }, brokerB: { name: '', enabled: false }, brokerC: { name: '', enabled: false }, brokerD: { name: '', enabled: false }, brokerE: { name: '', enabled: false } };
        const keys = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
        ib.active_brokers.forEach((broker, index) => {
          if (index < 5) newCfg[keys[index]] = { name: brokerNameMap[broker] || broker, enabled: true };
        });
        setBrokerConfig(newCfg);
      }
    };
    if (ib?.id) loadBrokerConfig();
  }, [ib?.id, ib?.active_brokers]);

  // Summary Modal state
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryPeriod, setSummaryPeriod] = usePersistedState(`${PF_P}:summaryPeriod`, 'weekly');
  const [summaryWeekIndex, setSummaryWeekIndex] = useState(0);
  const [summaryDepartment, setSummaryDepartment] = useState('all');

  // KPI Targets from Channel Settings
  const [kpiTargets, setKpiTargets] = useState({
    marketing: {},
    sales: {},
    operations: {}
  });
  
  // Fetch KPI targets from API
  useEffect(() => {
    const fetchKpiTargets = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !ib?.id) return;
        
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();
        
        const response = await axios.get(
          `${API}/api/kpi-targets/${ib.id}/${currentYear}/${currentMonth}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data) {
          setKpiTargets(response.data);
        }
      } catch (error) {
        console.log('Using default KPI targets');
        // Fallback to localStorage
        const saved = localStorage.getItem(`kpi_targets_${ib?.id}`);
        if (saved) {
          const data = JSON.parse(saved);
          const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
          setKpiTargets(data[monthKey] || {});
        }
      }
    };
    
    fetchKpiTargets();
  }, [ib?.id, currentDate]);

  // Calculate dateFrom and dateTo based on dateRange
  const { dateFrom, dateTo } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today (midnight)
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    const defaultStart = startOfMonth(now);
    switch(dateRange) {
      case 'today': return { dateFrom: today, dateTo: endOfToday };
      case 'yesterday': {
        const yd = subDays(today, 1);
        return { dateFrom: yd, dateTo: new Date(yd.getFullYear(), yd.getMonth(), yd.getDate(), 23, 59, 59) };
      }
      case 'this-week': return { dateFrom: startOfWeek(now, { weekStartsOn: 1 }), dateTo: endOfToday };
      case 'last-week':
        const lastWeekStart = startOfWeek(subDays(now, 7), { weekStartsOn: 1 });
        return { dateFrom: lastWeekStart, dateTo: endOfWeek(lastWeekStart, { weekStartsOn: 1 }) };
      case 'this-month':
      case 'mtd': return { dateFrom: startOfMonth(now), dateTo: endOfToday };
      case 'last-month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return { dateFrom: lastMonth, dateTo: lastMonthEnd };
      case 'full': 
        // Full maximum - combine 2025 Google Sheet data + 2026 Daily Data
        return { dateFrom: new Date('2025-01-01'), dateTo: endOfToday };
      case 'custom': return { dateFrom: customDateFrom || defaultStart, dateTo: customDateTo || endOfToday };
      // 2025 specific ranges
      case '2025-full': return { dateFrom: new Date('2025-01-01'), dateTo: new Date('2025-12-31T23:59:59') };
      case '2025-q1': return { dateFrom: new Date('2025-01-01'), dateTo: new Date('2025-03-31T23:59:59') };
      case '2025-q2': return { dateFrom: new Date('2025-04-01'), dateTo: new Date('2025-06-30T23:59:59') };
      case '2025-q3': return { dateFrom: new Date('2025-07-01'), dateTo: new Date('2025-09-30T23:59:59') };
      case '2025-q4': return { dateFrom: new Date('2025-10-01'), dateTo: new Date('2025-12-31T23:59:59') };
      // 2026 specific ranges
      case '2026-jan': return { dateFrom: new Date('2026-01-01'), dateTo: new Date('2026-01-31T23:59:59') };
      default: return { dateFrom: defaultStart, dateTo: endOfToday };
    }
  }, [dateRange, customDateFrom, customDateTo]);
  
  // Load Daily Data from MongoDB API (daily_data_2026 collection)
  useEffect(() => {
    if (!ib?.id) return;
    const fetchDailyData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch all relevant months dynamically (supports any year)
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        
        const relevantMonths = [];
        for (let y = 2026; y <= currentYear; y++) {
          const maxMonth = y === currentYear ? currentMonth : 12;
          for (let m = 1; m <= maxMonth; m++) {
            relevantMonths.push(`${y}-${String(m).padStart(2, '0')}`);
          }
        }
        
        let allMarketing = [];
        let allSales = [];
        
        const promises = relevantMonths.map(month => 
          axios.get(`${API}/api/historical/2026/daily-data/${ib.id}?month=${month}`, { headers }).catch(() => null)
        );
        const results = await Promise.all(promises);
        
        for (const res of results) {
          if (res?.data) {
            const d = res.data;
            if (d.marketing?.length) allMarketing = [...allMarketing, ...d.marketing];
            if (d.sales?.length) allSales = [...allSales, ...d.sales];
          }
        }
        
        setDailyData({ marketing: allMarketing, sales: allSales });
      } catch (e) {
        console.log('Daily data fetch failed:', e);
        setDailyData({ marketing: [], sales: [] });
      }
    };
    fetchDailyData();
  }, [ib?.id]);
  
  // Aggregate Daily Data based on date range
  const aggregatedDailyData = useMemo(() => {
    if (!dailyData.marketing?.length && !dailyData.sales?.length) return null;
    
    // Filter by date range
    const filterByDateRange = (data) => {
      if (!data?.length) return [];
      return data.filter(item => {
        if (!item.date) return false;
        const itemDate = new Date(item.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
    };
    
    const filteredMarketing = filterByDateRange(dailyData.marketing);
    const filteredSales = filterByDateRange(dailyData.sales);
    
    if (!filteredMarketing.length && !filteredSales.length) return null;
    
    // Helper: best value for a broker field, reading BOTH storage shapes but
    // NEVER adding them together (dict `vantage` can already contain vantage+xm).
    // Mirrors backend utils/data_helpers._resolve_total.
    const enabledKeys = Object.entries(brokerConfig).filter(([, v]) => v.enabled).map(([k]) => k);
    const enabledIds = new Set([
      ...enabledKeys.map((k) => k.toLowerCase()),
      ...enabledKeys.map((k) => String(brokerConfig?.[k]?.name || '').toLowerCase().trim()).filter(Boolean),
    ]);
    const brokerFieldValue = (bd, field) => {
      let v;
      if (field === 'commission') v = bd.commission_usd ?? bd.commission;
      else if (field === 'deposit') v = bd.deposit_usd ?? bd.deposit;
      else v = bd[field];
      return safeNum(v || 0);
    };
    const sumEnabledBrokerField = (row, field) => {
      if (!row || typeof row !== 'object') return 0;

      const hasDict = row.brokers !== undefined && typeof row.brokers === 'object' && row.brokers !== null;
      let dictTotal = 0;
      let dictCount = 0;
      if (hasDict) {
        for (const [k, bd] of Object.entries(row.brokers)) {
          if (!bd || typeof bd !== 'object') continue;
          if (enabledIds.size > 0) {
            const key = String(k).toLowerCase().trim();
            const name = String(bd.name || '').toLowerCase().trim();
            if (!enabledIds.has(key) && !enabledIds.has(name)) continue;
          }
          const v = brokerFieldValue(bd, field);
          dictTotal += v;
          if (v) dictCount += 1;
        }
      }

      let legacy = 0;
      let legacyCount = 0;
      const keys = enabledKeys.length > 0 ? enabledKeys : ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
      for (const key of keys) {
        const bd = row[key];
        if (bd && typeof bd === 'object') {
          const v = brokerFieldValue(bd, field);
          legacy += v;
          if (v) legacyCount += 1;
        }
      }

      // Same rule as backend _resolve_total: trust the name-keyed dict when it
      // covers at least as many brokers as the legacy slots.
      const best = hasDict && dictCount >= legacyCount ? dictTotal : Math.max(dictTotal, legacy);
      if (best > 0 || hasDict || legacy > 0) return best;

      if (field === 'commission') return safeNum(row.total_commission_usd);
      if (field === 'deposit') return safeNum(row.total_deposit_usd);
      if (field === 'active') return safeNum(row.total_active_client);
      if (field === 'lots') return safeNum(row.total_traded_lot);
      return 0;
    };

    // Aggregate
    return {
      total_ad_spent: filteredMarketing.reduce((sum, d) => sum + safeNum(d.spend), 0),
      total_impressions: filteredMarketing.reduce((sum, d) => sum + safeNum(d.impressions), 0),
      total_clicks: filteredMarketing.reduce((sum, d) => sum + safeNum(d.clicks) + safeInt(d.cta), 0),
      total_lpv: filteredMarketing.reduce((sum, d) => sum + safeInt(d.lpv), 0),
      total_cta: filteredMarketing.reduce((sum, d) => sum + safeInt(d.cta), 0),
      total_subscribers: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsTG) + safeInt(d.subsBot), 0),
      total_subs_tg: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsTG), 0),
      total_subs_bot: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsBot), 0),
      total_subs_free_trial: filteredMarketing.reduce((sum, d) => sum + safeInt(d.subsFreeTrial), 0),
      total_leads_m: filteredSales.reduce((sum, d) => sum + safeNum(d.leadsM), 0),
      total_leads_s: filteredSales.reduce((sum, d) => sum + safeNum(d.leadsS), 0),
      total_leads_ib: filteredSales.reduce((sum, d) => sum + safeNum(d.leadsIB), 0),
      total_leads: filteredSales.reduce((sum, d) => sum + sumLeads(d), 0),
      total_closing: filteredSales.reduce((sum, d) => sum + sumClosed(d), 0),
      total_active_client: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'active'), 0),
      total_traded_lot: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'lots'), 0),
      total_commission: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'commission') * getRateForDate(d.date), 0),
      total_commission_usd: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'commission'), 0),
      total_deposit: filteredSales.reduce((sum, d) => sum + sumEnabledBrokerField(d, 'deposit'), 0),
      // Sales funnel data
      greeting: filteredSales.reduce((sum, d) => sum + (d.greeting || 0), 0),
      oneTick: filteredSales.reduce((sum, d) => sum + (d.oneTick || 0), 0),
      registration: filteredSales.reduce((sum, d) => sum + (d.registration || 0), 0),
      broker: filteredSales.reduce((sum, d) => sum + (d.broker || 0), 0),
      priceOffer: filteredSales.reduce((sum, d) => sum + (d.priceOffer || 0), 0),
      record_count: filteredMarketing.length + filteredSales.length,
      daily_data: filteredMarketing, // For trend chart
      // Per-broker breakdown for Com/Lot
      broker_breakdown: (() => {
        const bt = {};
        filteredSales.forEach(d => {
          if (d.brokers && typeof d.brokers === 'object') {
            Object.entries(d.brokers).forEach(([k, b]) => {
              if (!b || typeof b !== 'object') return;
              const name = (b.name || k).toUpperCase();
              if (!bt[name]) bt[name] = { lots: 0, commission_usd: 0 };
              bt[name].lots += safeNum(b.lots);
              bt[name].commission_usd += safeNum(b.commission_usd) || safeNum(b.commission);
            });
          } else {
            for (const bk of ['brokerA','brokerB','brokerC','brokerD','brokerE']) {
              if (d[bk] && typeof d[bk] === 'object') {
                const name = (brokerConfig[bk]?.name || bk).toUpperCase();
                if (!bt[name]) bt[name] = { lots: 0, commission_usd: 0 };
                bt[name].lots += safeNum(d[bk].lots);
                bt[name].commission_usd += safeNum(d[bk].commission);
              }
            }
          }
        });
        return Object.entries(bt).map(([name, d]) => ({
          name, lots: d.lots, commission_usd: d.commission_usd,
          com_per_lot: d.lots > 0 ? Math.round((d.commission_usd / d.lots) * 100) / 100 : 0
        })).filter(b => b.lots > 0 || b.commission_usd > 0);
      })(),
    };
  }, [dailyData, dateFrom, dateTo, brokerConfig, getRateForDate]);

  // Previous month data for % comparison
  const prevMonthData = useMemo(() => {
    if (!dailyData.marketing?.length && !dailyData.sales?.length) return {};
    const now = new Date();
    const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const pmDay = Math.min(now.getDate(), new Date(now.getFullYear(), now.getMonth(), 0).getDate());
    const pmEnd = new Date(now.getFullYear(), now.getMonth() - 1, pmDay);
    const pM = (dailyData.marketing||[]).filter(d => { const dt=new Date(d.date); return dt>=pm&&dt<=pmEnd; });
    const pS = (dailyData.sales||[]).filter(d => { const dt=new Date(d.date); return dt>=pm&&dt<=pmEnd; });
    return {
      total_ad_spent: pM.reduce((s,d) => s+safeNum(d.spend), 0),
      subs_tg: pM.reduce((s,d) => s+safeInt(d.subsTG), 0),
      leads: pM.reduce((s,d) => s+safeInt(d.leads), 0),
      total_commission: pS.reduce((s,d) => { let t=0; const ek=Object.entries(brokerConfig).filter(([,v])=>v.enabled).map(([k])=>k); for(const k of ek){const bd=d[k]; if(bd&&typeof bd==='object') t+=safeNum(bd.commission);} return s+t*getRateForDate(d.date); }, 0),
      total_active_client: pS.reduce((s,d) => { let t=0; const ek=Object.entries(brokerConfig).filter(([,v])=>v.enabled).map(([k])=>k); for(const k of ek){const bd=d[k]; if(bd&&typeof bd==='object') t+=safeInt(bd.active);} return s+t; }, 0),
      total_traded_lot: pS.reduce((s,d) => { let t=0; const ek=Object.entries(brokerConfig).filter(([,v])=>v.enabled).map(([k])=>k); for(const k of ek){const bd=d[k]; if(bd&&typeof bd==='object') t+=safeNum(bd.lots);} return s+t; }, 0),
    };
  }, [dailyData, brokerConfig, getRateForDate]);
  const calcTrend = (c, p) => { if(!p||p===0) return c>0?100:0; return ((c-p)/p*100); };
  // Combined active data source
  // For "Full Maximum", COMBINE 2025 API data + 2026 localStorage data
  // For 2026 ranges, MERGE aggregatedDailyData with historicalData (MTProto) so MTProto fields are not lost
  const activeData = useMemo(() => {
    // Full Maximum: combine both 2025 API data and 2026 localStorage data
    if (dateRange === 'full') {
      const combined = {
        total_ad_spent: (historicalData?.total_ad_spent || 0) + (aggregatedDailyData?.total_ad_spent || 0),
        total_impressions: (historicalData?.total_impressions || 0) + (aggregatedDailyData?.total_impressions || 0),
        total_clicks: (historicalData?.total_clicks || 0) + (aggregatedDailyData?.total_clicks || 0),
        total_lpv: (historicalData?.total_lpv || 0) + (aggregatedDailyData?.total_lpv || 0),
        total_cta: (historicalData?.total_cta || 0) + (aggregatedDailyData?.total_cta || 0),
        total_subscribers: (historicalData?.total_subscribers || 0) + (aggregatedDailyData?.total_subscribers || 0),
        total_subs_tg: (historicalData?.total_subs_tg || 0) + (aggregatedDailyData?.total_subs_tg || 0),
        total_subs_bot: (historicalData?.total_subs_bot || 0) + (aggregatedDailyData?.total_subs_bot || 0),
        // Leads per department
        total_leads_m: (historicalData?.total_leads_m || 0) + (aggregatedDailyData?.total_leads_m || 0),
        total_leads_s: (historicalData?.total_leads_s || 0) + (aggregatedDailyData?.total_leads_s || 0),
        total_leads_ib: (historicalData?.total_leads_ib || 0) + (aggregatedDailyData?.total_leads_ib || 0),
        total_leads: (historicalData?.total_leads || 0) + (aggregatedDailyData?.total_leads || 0),
        total_closing: (historicalData?.total_closing || 0) + (aggregatedDailyData?.total_closing || 0),
        total_active_client: (historicalData?.total_active_client || 0) + (aggregatedDailyData?.total_active_client || 0),
        total_traded_lot: (historicalData?.total_traded_lot || 0) + (aggregatedDailyData?.total_traded_lot || 0),
        total_commission: (historicalData?.total_commission || 0) + (aggregatedDailyData?.total_commission || 0),
        total_deposit: (historicalData?.total_deposit || 0) + (aggregatedDailyData?.total_deposit || 0),
        greeting: (historicalData?.greeting || 0) + (aggregatedDailyData?.greeting || 0),
        oneTick: (historicalData?.oneTick || 0) + (aggregatedDailyData?.oneTick || 0),
        registration: (historicalData?.registration || 0) + (aggregatedDailyData?.registration || 0),
        broker: (historicalData?.broker || 0) + (aggregatedDailyData?.broker || 0),
        priceOffer: (historicalData?.priceOffer || 0) + (aggregatedDailyData?.priceOffer || 0),
        record_count: (historicalData?.record_count || 0) + (aggregatedDailyData?.record_count || 0),
        daily_data: aggregatedDailyData?.daily_data || [],
        broker_breakdown: aggregatedDailyData?.broker_breakdown || historicalData?.broker_breakdown || [],
        // Include MTProto data (Public channel only for Operations funnel)
        telegram_subscribers: historicalData?.telegram_subscribers || 0,
        avg_views_per_post: historicalData?.avg_views_per_post || 0,
        avg_reactions_per_post: historicalData?.avg_reactions_per_post || 0,
        content_posts_public: historicalData?.content_posts_public || 0,
        content_posts_vip: historicalData?.content_posts_vip || 0,
        engagement_rate_public: historicalData?.engagement_rate_public || 0,
        total_signals: historicalData?.total_signals || 0,
        signal_win_rate: historicalData?.signal_win_rate || 0,
      };
      if (combined.record_count > 0 || historicalData || aggregatedDailyData) {
        return combined;
      }
      return null;
    }
    
    // For 2026 ranges OR when historicalData has MTProto data:
    // MERGE aggregatedDailyData with historicalData so MTProto fields (telegram_subscribers, avg_views, etc.) are preserved
    if (aggregatedDailyData && aggregatedDailyData.record_count > 0) {
      // If we also have historicalData with MTProto data, merge them
      if (historicalData && (historicalData.telegram_subscribers || historicalData.avg_views_per_post)) {
        return {
          ...aggregatedDailyData,
          // Overlay MTProto data from historicalData (Public channel data for Operations funnel)
          telegram_subscribers: historicalData.telegram_subscribers || 0,
          avg_views_per_post: historicalData.avg_views_per_post || 0,
          avg_reactions_per_post: historicalData.avg_reactions_per_post || 0,
          content_posts_public: historicalData.content_posts_public || 0,
          content_posts_vip: historicalData.content_posts_vip || 0,
          engagement_rate_public: historicalData.engagement_rate_public || 0,
          engagement_rate_vip: historicalData.engagement_rate_vip || 0,
          total_signals: historicalData.total_signals || 0,
          signal_win_rate: historicalData.signal_win_rate || 0,
        };
      }
      return aggregatedDailyData;
    }
    
    // For specific date filters (today, yesterday, this-week, last-week, custom),
    // if aggregatedDailyData is null it means NO data exists for that range — return null (don't fallback to historicalData)
    const specificDateRanges = ['today', 'yesterday', 'this-week', 'last-week', 'custom'];
    if (specificDateRanges.includes(dateRange)) {
      return null;
    }
    
    // For broader ranges (MTD, last-month, 2025 ranges), use historicalData (includes MTProto data)
    if (historicalData) return historicalData;
    
    // No data for selected range
    return null;
  }, [dateRange, historicalData, aggregatedDailyData]);

  // Fetch historical 2025 data (from backend API)
  useEffect(() => {
    const fetchHistoricalData = async () => {
      if (!ib?.id) return;
      
      const startYear = dateFrom?.getFullYear();
      const endYear = dateTo?.getFullYear();
      console.log('fetchHistoricalData - dateRange:', dateRange, 'startYear:', startYear, 'endYear:', endYear);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch 2026 data (from Daily Data)
      if (startYear === 2026 || endYear === 2026 || dateRange?.startsWith('2026')) {
        console.log('Fetching 2026 data...');
        setIsLoadingData(true);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/historical/2026/daily-data/${ib.id}`,
            { headers }
          );
          console.log('2026 API response:', response.data);
          
          // MTProto data is now included in response
          const mtprotoData = {
            telegram_subscribers: response.data.telegram_subscribers || 0,
            avg_views_per_post: response.data.avg_views_per_post || 0,
            avg_reactions_per_post: response.data.avg_reactions_per_post || 0,
            content_posts_public: response.data.content_posts_public || 0,
            content_posts_vip: response.data.content_posts_vip || 0,
            engagement_rate_public: response.data.engagement_rate_public || 0,
            total_signals: response.data.total_signals || 0,
            signal_win_rate: response.data.signal_win_rate || 0,
          };
          console.log('MTProto data extracted:', mtprotoData);
          
          if (response.data.imported && response.data.marketing?.length > 0) {
            const marketing = response.data.marketing || [];
            const sales = response.data.sales || [];
            const eKeys = Object.entries(brokerConfig).filter(([, v]) => v.enabled).map(([k]) => k);
            
            // Aggregate data
            const aggregated = {
              total_ad_spent: marketing.reduce((sum, d) => {
                const spend = d.spend || {};
                return sum + Object.values(spend).reduce((s, v) => s + (v || 0), 0);
              }, 0),
              total_lpv: marketing.reduce((sum, d) => sum + (d.lpv || 0), 0),
              total_clicks: marketing.reduce((sum, d) => sum + (d.cta || 0), 0),
              total_subscribers: marketing.reduce((sum, d) => sum + (d.subsTG || 0) + (d.subsBot || 0), 0),
              total_leads: marketing.reduce((sum, d) => sum + (d.leads || 0), 0),
              total_closing: marketing.reduce((sum, d) => sum + (d.closed || 0), 0),
              total_active_client: sales.reduce((sum, d) => {
                let t = 0; for (const k of eKeys) { const bd = d[k]; if (bd && typeof bd === 'object') t += safeInt(bd.active); } return sum + t;
              }, 0),
              total_traded_lot: sales.reduce((sum, d) => {
                let t = 0; for (const k of eKeys) { const bd = d[k]; if (bd && typeof bd === 'object') t += safeNum(bd.lots); } return sum + t;
              }, 0),
              total_commission: sales.reduce((sum, d) => {
                let t = 0; for (const k of eKeys) { const bd = d[k]; if (bd && typeof bd === 'object') t += safeNum(bd.commission); } return sum + t;
              }, 0),
              total_deposit: sales.reduce((sum, d) => {
                let t = 0; for (const k of eKeys) { const bd = d[k]; if (bd && typeof bd === 'object') t += safeNum(bd.deposit); } return sum + t;
              }, 0),
              // Per-broker breakdown for Com/Lot
              broker_breakdown: (() => {
                const bt = {};
                sales.forEach(d => {
                  if (d.brokers && typeof d.brokers === 'object') {
                    Object.entries(d.brokers).forEach(([k, b]) => {
                      if (!b || typeof b !== 'object') return;
                      const name = (b.name || k).toUpperCase();
                      if (!bt[name]) bt[name] = { lots: 0, commission_usd: 0 };
                      bt[name].lots += safeNum(b.lots);
                      bt[name].commission_usd += safeNum(b.commission_usd) || safeNum(b.commission);
                    });
                  } else {
                    for (const bk of ['brokerA','brokerB','brokerC','brokerD','brokerE']) {
                      if (d[bk] && typeof d[bk] === 'object') {
                        const name = (brokerConfig[bk]?.name || bk).toUpperCase();
                        if (!bt[name]) bt[name] = { lots: 0, commission_usd: 0 };
                        bt[name].lots += safeNum(d[bk].lots);
                        bt[name].commission_usd += safeNum(d[bk].commission);
                      }
                    }
                  }
                });
                return Object.entries(bt).map(([name, d]) => ({
                  name, lots: d.lots, commission_usd: d.commission_usd,
                  com_per_lot: d.lots > 0 ? Math.round((d.commission_usd / d.lots) * 100) / 100 : 0
                })).filter(b => b.lots > 0 || b.commission_usd > 0);
              })(),
              // Merge MTProto data
              ...mtprotoData
            };
            setHistoricalData(aggregated);
          } else {
            // Even without Google Sheet data, still use MTProto data
            setHistoricalData(mtprotoData);
          }
        } catch (error) {
          console.error('Failed to fetch 2026 data:', error);
          setHistoricalData(null);
        } finally {
          setIsLoadingData(false);
        }
      }
      // Fetch 2025 data when: 2025 range selected OR "Full Maximum" (which includes 2025)
      else if (startYear === 2025 || endYear === 2025 || dateRange?.startsWith('2025') || dateRange === 'full') {
        setIsLoadingData(true);
        try {
          // For "full" range, always fetch from 2025-01-01 to 2025-12-31
          const apiStartDate = dateRange === 'full' ? '2025-01-01' : format(dateFrom, 'yyyy-MM-dd');
          const apiEndDate = dateRange === 'full' ? '2025-12-31' : format(dateTo, 'yyyy-MM-dd');
          
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/historical/2025/${ib.id}/summary`,
            { 
              params: { start_date: apiStartDate, end_date: apiEndDate },
              headers
            }
          );
          setHistoricalData(response.data);
          console.log('Historical data loaded:', response.data);
        } catch (error) {
          console.error('Failed to fetch historical data:', error);
          setHistoricalData(null);
        } finally {
          setIsLoadingData(false);
        }
      } else {
        setHistoricalData(null);
      }
    };
    
    fetchHistoricalData();
  }, [ib?.id, dateFrom, dateTo, dateRange]);

  // Load KPI Configuration from localStorage (shared with Admin Panel)
  const getKpiConfig = () => {
    try {
      const stored = localStorage.getItem('kpiConfig');
      if (stored) return JSON.parse(stored);
    } catch (e) {
      console.warn('Failed to load KPI config:', e);
    }
    // Default weights
    return {
      healthScoreWeights: {
        marketing: { targetAchievement: 25, conversionFunnel: 25, conversionRates: 25, adsEfficiency: 25 },
        sales: { targetAchievement: 30, pipeline: 25, closingRate: 25, commission: 20 },
        operations: { targetAchievement: 30, engagement: 25, activity: 25, retention: 20 },
      },
      conversionThresholds: {
        ctaConversion: { poor: 10, average: 20, good: 35, excellent: 50 },
        subsConversion: { poor: 5, average: 15, good: 30, excellent: 45 },
        leadsConversion: { poor: 15, average: 25, good: 40, excellent: 55 },
        closingRate: { poor: 20, average: 35, good: 50, excellent: 65 },
      }
    };
  };
  
  const kpiConfig = getKpiConfig();
  
  // Calculate Marketing Health Score based on real data
  const calculateMarketingHealthScore = (data, weights) => {
    if (!data) return 0;
    
    let score = 0;
    const w = weights || { targetAchievement: 25, conversionFunnel: 25, conversionRates: 25, adsEfficiency: 25 };
    
    // 1. Target Achievement (based on having data)
    const hasSpend = (data.total_ad_spent || 0) > 0;
    const hasLeads = (data.total_leads || 0) > 0;
    const hasSubs = (data.total_subscribers || 0) > 0;
    const targetScore = ((hasSpend ? 40 : 0) + (hasLeads ? 30 : 0) + (hasSubs ? 30 : 0));
    score += (targetScore / 100) * w.targetAchievement;
    
    // 2. Conversion Funnel (LPV to Leads conversion)
    const lpv = data.total_lpv || 0;
    const clicks = data.total_clicks || 0;
    const leads = data.total_leads || 0;
    let funnelScore = 50; // base
    if (lpv > 0 && leads > 0) {
      const lpvToLeadRate = (leads / lpv) * 100;
      if (lpvToLeadRate >= 5) funnelScore = 100;
      else if (lpvToLeadRate >= 3) funnelScore = 80;
      else if (lpvToLeadRate >= 1) funnelScore = 60;
      else funnelScore = 40;
    }
    score += (funnelScore / 100) * w.conversionFunnel;
    
    // 3. Conversion Rates (CTR-like metrics)
    let conversionScore = 50;
    if (clicks > 0 && lpv > 0) {
      const clickToLpvRate = (lpv / clicks) * 100;
      if (clickToLpvRate >= 70) conversionScore = 100;
      else if (clickToLpvRate >= 50) conversionScore = 80;
      else if (clickToLpvRate >= 30) conversionScore = 60;
      else conversionScore = 40;
    }
    score += (conversionScore / 100) * w.conversionRates;
    
    // 4. Ads Efficiency (ROI based)
    let efficiencyScore = 50;
    const adSpent = data.total_ad_spent || 0;
    const commission = data.total_commission || 0;
    if (adSpent > 0) {
      const roi = (commission / adSpent) * 100;
      if (roi >= 200) efficiencyScore = 100;
      else if (roi >= 100) efficiencyScore = 85;
      else if (roi >= 50) efficiencyScore = 70;
      else if (roi >= 20) efficiencyScore = 55;
      else efficiencyScore = 35;
    }
    score += (efficiencyScore / 100) * w.adsEfficiency;
    
    return Math.round(score);
  };
  
  // Calculate Sales Health Score based on real data
  const calculateSalesHealthScore = (data, weights) => {
    if (!data) return 0;
    
    let score = 0;
    const w = weights || { targetAchievement: 30, pipeline: 25, closingRate: 25, commission: 20 };
    
    // 1. Target Achievement (having sales data)
    const hasLeads = (data.total_leads || 0) > 0;
    const hasClosing = (data.total_closing || 0) > 0;
    const hasDeposit = (data.total_deposit || 0) > 0;
    const targetScore = ((hasLeads ? 35 : 0) + (hasClosing ? 35 : 0) + (hasDeposit ? 30 : 0));
    score += (targetScore / 100) * w.targetAchievement;
    
    // 2. Pipeline Health (lead to closing flow)
    let pipelineScore = 50;
    const leads = data.total_leads || 0;
    const closing = data.total_closing || 0;
    if (leads > 0 && closing > 0) {
      // Good pipeline if we have movement
      pipelineScore = Math.min(100, 50 + (closing / leads) * 100);
    }
    score += (pipelineScore / 100) * w.pipeline;
    
    // 3. Closing Rate
    let closingScore = 50;
    if (leads > 0) {
      const closingRate = (closing / leads) * 100;
      if (closingRate >= 40) closingScore = 100;
      else if (closingRate >= 30) closingScore = 85;
      else if (closingRate >= 20) closingScore = 70;
      else if (closingRate >= 10) closingScore = 55;
      else closingScore = 35;
    }
    score += (closingScore / 100) * w.closingRate;
    
    // 4. Commission Performance
    let commissionScore = 50;
    const commission = data.total_commission || 0;
    const deposit = data.total_deposit || 0;
    if (closing > 0) {
      const avgCommissionPerClient = commission / closing;
      if (avgCommissionPerClient >= 500) commissionScore = 100;
      else if (avgCommissionPerClient >= 300) commissionScore = 85;
      else if (avgCommissionPerClient >= 150) commissionScore = 70;
      else if (avgCommissionPerClient >= 50) commissionScore = 55;
      else commissionScore = 40;
    }
    score += (commissionScore / 100) * w.commission;
    
    return Math.round(score);
  };
  
  // Calculate Operations Health Score based on real data
  const calculateOperationsHealthScore = (data, weights) => {
    if (!data) return 0;
    
    let score = 0;
    const w = weights || { targetAchievement: 30, engagement: 25, activity: 25, retention: 20 };
    
    // 1. Target Achievement (having operational data)
    const hasActiveClient = (data.total_active_client || 0) > 0;
    const hasTradedLot = (data.total_traded_lot || 0) > 0;
    const hasCommission = (data.total_commission || 0) > 0;
    const targetScore = ((hasActiveClient ? 40 : 0) + (hasTradedLot ? 30 : 0) + (hasCommission ? 30 : 0));
    score += (targetScore / 100) * w.targetAchievement;
    
    // 2. Engagement (active clients ratio)
    let engagementScore = 50;
    const activeClient = data.total_active_client || 0;
    const closing = data.total_closing || 0;
    if (closing > 0) {
      const activeRatio = (activeClient / closing) * 100;
      if (activeRatio >= 80) engagementScore = 100;
      else if (activeRatio >= 60) engagementScore = 85;
      else if (activeRatio >= 40) engagementScore = 70;
      else if (activeRatio >= 20) engagementScore = 55;
      else engagementScore = 40;
    } else if (activeClient > 0) {
      engagementScore = 70; // Has active clients even without closing data
    }
    score += (engagementScore / 100) * w.engagement;
    
    // 3. Activity (traded lot per client)
    let activityScore = 50;
    const tradedLot = data.total_traded_lot || 0;
    if (activeClient > 0 && tradedLot > 0) {
      const lotPerClient = tradedLot / activeClient;
      if (lotPerClient >= 20) activityScore = 100;
      else if (lotPerClient >= 10) activityScore = 85;
      else if (lotPerClient >= 5) activityScore = 70;
      else if (lotPerClient >= 2) activityScore = 55;
      else activityScore = 40;
    }
    score += (activityScore / 100) * w.activity;
    
    // 4. Retention (commission sustainability)
    let retentionScore = 50;
    const commission = data.total_commission || 0;
    if (activeClient > 0 && commission > 0) {
      const commissionPerClient = commission / activeClient;
      if (commissionPerClient >= 200) retentionScore = 100;
      else if (commissionPerClient >= 100) retentionScore = 85;
      else if (commissionPerClient >= 50) retentionScore = 70;
      else if (commissionPerClient >= 20) retentionScore = 55;
      else retentionScore = 40;
    }
    score += (retentionScore / 100) * w.retention;
    
    return Math.round(score);
  };
  
  // Department Health Scores - NOW DYNAMIC based on activeData (from localStorage OR API)
  const departmentHealth = useMemo(() => {
    const weights = kpiConfig.healthScoreWeights;
    
    // If we have active data (from Daily Data localStorage or historical API), calculate real scores
    if (activeData && Object.keys(activeData).length > 0) {
      return [
        { 
          id: 'marketing', 
          name: 'Marketing', 
          score: calculateMarketingHealthScore(activeData, weights.marketing), 
          icon: Megaphone 
        },
        { 
          id: 'sales', 
          name: 'Sales', 
          score: calculateSalesHealthScore(activeData, weights.sales), 
          icon: Handshake 
        },
        { 
          id: 'operations', 
          name: 'Operations', 
          score: calculateOperationsHealthScore(activeData, weights.operations), 
          icon: Settings 
        },
      ];
    }
    
    // Default scores when no data (for non-FIRA IND or current period)
    return [
      { id: 'marketing', name: 'Marketing', score: isFiraInd ? 0 : 78, icon: Megaphone },
      { id: 'sales', name: 'Sales', score: isFiraInd ? 0 : 52, icon: Handshake },
      { id: 'operations', name: 'Operations', score: isFiraInd ? 0 : 92, icon: Settings },
    ];
  }, [activeData, kpiConfig, isFiraInd]);

  const tabs = [
    { id: 'marketing', label: 'Marketing', icon: Megaphone },
    { id: 'sales', label: 'Sales', icon: Handshake },
    { id: 'operations', label: 'Operations', icon: Settings },
  ];

  const getDateRangeLabel = () => {
    if (dateRange === 'mtd') return `MTD: ${format(dateFrom, 'dd/MM')} - ${format(dateTo, 'dd/MM')}`;
    if (dateRange === 'custom' && dateFrom && dateTo) return `${format(dateFrom, 'dd/MM')} - ${format(dateTo, 'dd/MM')}`;
    const labels = {
      'today': 'Today', 'yesterday': 'Yesterday', 'this-week': 'This Week',
      'last-week': 'Last Week', 'this-month': 'This Month', 'last-month': 'Last Month', 'full': 'Full Maximum',
      '2025-full': '📊 Full Year 2025', '2025-q1': '📊 Q1 2025', '2025-q2': '📊 Q2 2025', 
      '2025-q3': '📊 Q3 2025', '2025-q4': '📊 Q4 2025', '2026-jan': '📈 Jan 2026',
    };
    return labels[dateRange] || 'MTD';
  };

  return (
    <>
      {/* Sticky Header */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Channel Performance</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">Department-level Diagnosis & Metrics</p>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Button 
              variant="outline"
              size="sm" 
              className="border-[hsl(var(--border))] hover:border-[#F59E0B] hover:bg-[#F59E0B]/10 text-white text-xs"
              onClick={() => setShowSummaryModal(true)}
            >
              <FileText size={14} className="mr-1.5" />
              SUMMARY
            </Button>

            <Button variant="outline" size="icon" className="border-[hsl(var(--border))] hover:border-[#E63946] hover:bg-[#E63946]/10 text-white h-8 w-8">
              <Bell size={14} />
            </Button>

            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-auto sm:w-56 justify-start bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--surface-elevated))] text-xs">
                  <CalendarIcon size={14} className="mr-1.5" />
                  {getDateRangeLabel()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" align="end">
                <div className="p-4 border-b border-[hsl(var(--border))]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-3">Quick Select</p>
                  <div className="grid grid-cols-2 gap-2">
                    {['today', 'yesterday', 'this-week', 'last-week', 'this-month', 'last-month'].map((range) => (
                      <Button 
                        key={range} 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setDateRange(range)} 
                        className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start ${dateRange === range ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}
                      >
                        {range.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Button>
                    ))}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setDateRange('full')} 
                      className={`text-white hover:bg-[hsl(var(--surface-elevated))] col-span-2 justify-start ${dateRange === 'full' ? 'bg-[#E63946]/20 text-[#E63946]' : ''}`}
                    >
                      Full Maximum
                    </Button>
                  </div>
                </div>
                {/* 2025 Historical Data Section */}
                <div className="p-4 border-t border-[hsl(var(--border))]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#F59E0B] mb-3">📊 Historical 2025</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setDateRange('2025-full')} 
                      className={`text-white hover:bg-[hsl(var(--surface-elevated))] col-span-2 justify-start ${dateRange === '2025-full' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : ''}`}
                    >
                      Full Year 2025
                    </Button>
                    {['2025-q1', '2025-q2', '2025-q3', '2025-q4'].map((q) => (
                      <Button 
                        key={q} 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setDateRange(q)} 
                        className={`text-white hover:bg-[hsl(var(--surface-elevated))] justify-start ${dateRange === q ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : ''}`}
                      >
                        {q.replace('2025-', '').toUpperCase()} 2025
                      </Button>
                    ))}
                  </div>
                </div>
                {/* Custom Date Range Picker */}
                <div className="p-4 border-t border-[hsl(var(--border))]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-3">Custom Range</p>
                  <Popover 
                    open={showCustomDatePicker} 
                    onOpenChange={(open) => {
                      // Reset selection when opening picker
                      if (open) {
                        setCustomDateFrom(null);
                        setCustomDateTo(null);
                      }
                      setShowCustomDatePicker(open);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className={`w-full justify-start border-[hsl(var(--border))] text-white ${dateRange === 'custom' ? 'bg-[#E63946]/20 border-[#E63946]' : ''}`}
                      >
                        <CalendarIcon size={14} className="mr-2" />
                        {dateRange === 'custom' && customDateFrom && customDateTo 
                          ? `${format(customDateFrom, 'dd MMM yyyy')} - ${format(customDateTo, 'dd MMM yyyy')}`
                          : 'Pick custom dates'
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" align="center" side="left">
                      <Calendar
                        mode="range"
                        selected={{ from: customDateFrom, to: customDateTo }}
                        onSelect={(range) => {
                          if (range?.from && !range?.to) {
                            // First click - set start date
                            setCustomDateFrom(range.from);
                            setCustomDateTo(null);
                          } else if (range?.from && range?.to) {
                            // Second click - set end date and close automatically
                            setCustomDateFrom(range.from);
                            setCustomDateTo(range.to);
                            setDateRange('custom');
                            setShowCustomDatePicker(false);
                          }
                        }}
                        className="rounded-md"
                        numberOfMonths={1}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-8 py-6 space-y-6 max-w-[1600px]">
          
          {/* DEPARTMENT HEALTH SCORE */}
          <SectionCard>
            <SectionHeader 
              icon={Activity} 
              title="Department Health Score" 
              subtitle="Overall Performance by Department" 
            />
            <div className="p-6">
              {isLoadingData && (
                <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg">
                  <Loader2 size={14} className="text-[#F59E0B] animate-spin" />
                  <span className="text-xs text-[#F59E0B] font-medium">
                    Mengira Health Score...
                  </span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {departmentHealth.map((dept) => {
                  const Icon = dept.icon;
                  const color = getPerformanceColor(dept.score, { excellent: 90, good: 80, average: 60 });
                  const status = getPerformanceStatus(dept.score, { excellent: 90, good: 80, average: 60 });
                  const statusLabel = dept.score === 0 ? 'NO DATA' : status.toUpperCase();
                  
                  return (
                    <div 
                      key={dept.id}
                      data-testid={`health-score-${dept.id}`}
                      className={`relative p-6 rounded-xl border transition-all cursor-pointer ${
                        activeTab === dept.id 
                          ? 'shadow-lg' 
                          : 'bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]'
                      }`}
                      style={{
                        backgroundColor: activeTab === dept.id ? `${color}15` : undefined,
                        borderColor: activeTab === dept.id ? `${color}40` : undefined,
                      }}
                      onClick={() => setActiveTab(dept.id)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${color}20` }}
                          >
                            <Icon size={24} style={{ color }} />
                          </div>
                          <div>
                            <p className="font-semibold text-white text-lg">{dept.name}</p>
                            <Badge 
                              className="text-xs mt-1 border-none"
                              style={{ backgroundColor: `${color}20`, color }}
                            >
                              {statusLabel}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-end gap-2">
                        <p className="text-5xl font-mono font-bold" style={{ color }}>
                          {isLoadingData ? <Loader2 size={40} className="animate-spin" /> : dept.score}
                        </p>
                        <p className="text-lg text-[#9CA3AF] mb-1">/ 100</p>
                      </div>
                      {/* Progress bar with performance color */}
                      <div className="mt-4 h-2 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${dept.score}%`, backgroundColor: color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </SectionCard>

          {/* TAB NAVIGATION */}
          <div className="flex gap-2 border-b border-[hsl(var(--border))] pb-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 -mb-[2px] ${
                    isActive 
                      ? 'text-[#E63946] border-[#E63946] bg-[#E63946]/5' 
                      : 'text-[#9CA3AF] border-transparent hover:text-white hover:bg-[hsl(var(--surface))]'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* TAB CONTENT - Now using activeData which combines localStorage + API data */}
          {activeTab === 'marketing' && <MarketingTab dateRange={dateRange} dateFrom={dateFrom} dateTo={dateTo} dailyData={dailyData} adjustment={adjustment} setAdjustment={setAdjustment} scenario={scenario} setScenario={setScenario} forecastPeriod={forecastPeriod} setForecastPeriod={setForecastPeriod} historicalData={activeData} isLoading={isLoadingData} isFiraInd={isFiraInd} usdToMyr={usdToMyr} kpiTargets={kpiTargets} kpiThresholds={kpiThresholds} />}
          {activeTab === 'sales' && <SalesTab dateRange={dateRange} dateFrom={dateFrom} dateTo={dateTo} dailyData={dailyData} adjustment={adjustment} setAdjustment={setAdjustment} scenario={scenario} setScenario={setScenario} forecastPeriod={forecastPeriod} setForecastPeriod={setForecastPeriod} historicalData={activeData} isLoading={isLoadingData} isFiraInd={isFiraInd} usdToMyr={usdToMyr} kpiTargets={kpiTargets} kpiThresholds={kpiThresholds} />}
          {activeTab === 'operations' && <OperationsTab ibId={ib?.id} dateRange={dateRange} dateFrom={dateFrom} dateTo={dateTo} dailyData={dailyData} adjustment={adjustment} setAdjustment={setAdjustment} scenario={scenario} setScenario={setScenario} forecastPeriod={forecastPeriod} setForecastPeriod={setForecastPeriod} historicalData={activeData} isLoading={isLoadingData} isFiraInd={isFiraInd} usdToMyr={usdToMyr} kpiTargets={kpiTargets} kpiThresholds={kpiThresholds} />}

        </div>
      </div>

      {/* SUMMARY REPORT MODAL */}
      <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden bg-[#FAFAFA] border-none p-0">
          <div className="bg-gradient-to-r from-[#1F2937] to-[#374151] p-6 text-white">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center">
                      <FileText size={24} />
                    </div>
                    <div>
                      <span>Channel Performance Report</span>
                      <p className="text-sm font-normal text-gray-300 mt-1">{ib?.name || 'Channel'} • {format(new Date(), 'MMMM yyyy')}</p>
                    </div>
                  </DialogTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10" onClick={() => window.print()}>
                    <Printer size={14} className="mr-2" />Print
                  </Button>
                  <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10" onClick={() => {
                    const report = `Channel Performance Report - ${format(new Date(), 'MMMM yyyy')}`;
                    navigator.clipboard.writeText(report);
                    alert('Report copied to clipboard!');
                  }}>
                    <Share2 size={14} className="mr-2" />Share
                  </Button>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Period & Department Selector */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Tabs value={summaryPeriod} onValueChange={(v) => { setSummaryPeriod(v); setSummaryWeekIndex(0); }}>
                  <TabsList className="bg-gray-200 p-1">
                    <TabsTrigger 
                      value="weekly" 
                      className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white data-[state=inactive]:text-gray-700 data-[state=inactive]:bg-transparent px-6 font-semibold"
                    >
                      Weekly
                    </TabsTrigger>
                    <TabsTrigger 
                      value="monthly" 
                      className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white data-[state=inactive]:text-gray-700 data-[state=inactive]:bg-transparent px-6 font-semibold"
                    >
                      Monthly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <Select value={summaryDepartment} onValueChange={setSummaryDepartment}>
                  <SelectTrigger className="w-40 bg-white border-gray-200">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {summaryPeriod === 'weekly' && (
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={() => setSummaryWeekIndex(Math.max(0, summaryWeekIndex - 1))} disabled={summaryWeekIndex === 0} className="border-gray-300 bg-white">
                    <ChevronLeft size={16} className="text-gray-700" />
                  </Button>
                  <span className="text-sm font-semibold text-gray-800 min-w-[100px] text-center bg-gray-100 px-4 py-2 rounded-lg">Week {summaryWeekIndex + 1}</span>
                  <Button variant="outline" size="sm" onClick={() => setSummaryWeekIndex(Math.min(3, summaryWeekIndex + 1))} disabled={summaryWeekIndex >= 3} className="border-gray-300 bg-white">
                    <ChevronRight size={16} className="text-gray-700" />
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Marketing Department Section */}
              {(summaryDepartment === 'all' || summaryDepartment === 'marketing') && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-[#E63946]/10 to-[#E63946]/5 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#E63946]/20 flex items-center justify-center"><Megaphone size={16} className="text-[#E63946]" /></div>
                      Marketing Department
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {[
                          { label: 'Spend', value: summaryPeriod === 'weekly' ? `RM${(2500 + summaryWeekIndex * 800).toLocaleString()}` : 'RM9,435', color: '#E63946' },
                          { label: 'Impressions', value: summaryPeriod === 'weekly' ? '154K' : '616K', color: '#3B82F6' },
                          { label: 'Clicks', value: summaryPeriod === 'weekly' ? '8.6K' : '34.6K', color: '#F97316' },
                          { label: 'Subscribers', value: summaryPeriod === 'weekly' ? (25 + summaryWeekIndex * 8).toString() : '100', color: '#10B981' },
                          { label: 'Leads (M)', value: summaryPeriod === 'weekly' ? (24 + summaryWeekIndex * 6).toString() : '99', color: '#F59E0B' },
                          { label: 'ROI', value: '65.7%', color: '#8B5CF6' },
                        ].map((m, i) => (
                          <div key={i} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                            <p className="text-[10px] uppercase text-gray-500 mb-1">{m.label}</p>
                            <p className="text-lg font-bold" style={{ color: m.color }}>{m.value}</p>
                          </div>
                        ))}
                      </div>
                      <div className="h-36">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={[{ n: 'Imp', v: 616 },{ n: 'Click', v: 34 },{ n: 'LPV', v: 26 },{ n: 'Subs', v: 100 },{ n: 'Lead', v: 99 }]}>
                            <XAxis dataKey="n" tick={{ fontSize: 10, fill: '#6B7280' }} />
                            <Area type="monotone" dataKey="v" stroke="#E63946" fill="#E6394620" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-800"><strong>Issue:</strong> CPL meningkat 15% - perlu optimize targeting audience</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sales Department Section */}
              {(summaryDepartment === 'all' || summaryDepartment === 'sales') && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-[#10B981]/10 to-[#10B981]/5 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#10B981]/20 flex items-center justify-center"><Handshake size={16} className="text-[#10B981]" /></div>
                      Sales Department
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {[
                          { label: 'Total Leads', value: summaryPeriod === 'weekly' ? (42 + summaryWeekIndex * 10).toString() : '169', color: '#3B82F6' },
                          { label: 'Closed', value: summaryPeriod === 'weekly' ? (11 + summaryWeekIndex * 4).toString() : '44', color: '#10B981' },
                          { label: 'Deposit', value: summaryPeriod === 'weekly' ? `$${(2800 + summaryWeekIndex * 900).toLocaleString()}` : '$11,303', color: '#F59E0B' },
                          { label: 'Close Rate', value: '26%', color: '#E63946' },
                          { label: 'CPL', value: 'RM55.80', color: '#F97316' },
                          { label: 'LTV', value: 'RM2,450', color: '#8B5CF6' },
                        ].map((m, i) => (
                          <div key={i} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                            <p className="text-[10px] uppercase text-gray-500 mb-1">{m.label}</p>
                            <p className="text-lg font-bold" style={{ color: m.color }}>{m.value}</p>
                          </div>
                        ))}
                      </div>
                      <div className="h-36">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie data={[{ name: 'Greeting', value: 169, color: '#E63946' },{ name: 'Reg', value: 120, color: '#F97316' },{ name: 'Broker', value: 98, color: '#F59E0B' },{ name: 'Closed', value: 44, color: '#10B981' }]} cx="50%" cy="50%" innerRadius={25} outerRadius={50} paddingAngle={2} dataKey="value">
                              {[{ color: '#E63946' },{ color: '#F97316' },{ color: '#F59E0B' },{ color: '#10B981' }].map((e, i) => (<Cell key={i} fill={e.color} />))}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }} />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs text-green-800"><strong>Highlight:</strong> Closing rate meningkat 3% - momentum positif!</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Operations Department Section */}
              {(summaryDepartment === 'all' || summaryDepartment === 'operations') && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-[#8B5CF6]/10 to-[#8B5CF6]/5 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center"><Settings size={16} className="text-[#8B5CF6]" /></div>
                      Operations Department
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {[
                          { label: 'Active Client', value: summaryPeriod === 'weekly' ? (11 + summaryWeekIndex * 3).toString() : '45', color: '#10B981' },
                          { label: 'Traded Lot', value: summaryPeriod === 'weekly' ? (24.5 + summaryWeekIndex * 8).toFixed(1) : '98.2', color: '#F59E0B' },
                          { label: 'Commission', value: summaryPeriod === 'weekly' ? `RM${(1550 + summaryWeekIndex * 500).toLocaleString()}` : 'RM6,200', color: '#E63946' },
                          { label: 'Live Session', value: summaryPeriod === 'weekly' ? (3 + summaryWeekIndex).toString() : '12', color: '#3B82F6' },
                          { label: 'Signals', value: summaryPeriod === 'weekly' ? (11 + summaryWeekIndex * 3).toString() : '45', color: '#8B5CF6' },
                          { label: 'Retention', value: '78%', color: '#F97316' },
                        ].map((m, i) => (
                          <div key={i} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                            <p className="text-[10px] uppercase text-gray-500 mb-1">{m.label}</p>
                            <p className="text-lg font-bold" style={{ color: m.color }}>{m.value}</p>
                          </div>
                        ))}
                      </div>
                      <div className="h-36">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={[{ w: 'W1', live: 3, sig: 12 },{ w: 'W2', live: 4, sig: 15 },{ w: 'W3', live: 2, sig: 10 },{ w: 'W4', live: 3, sig: 8 }]}>
                            <XAxis dataKey="w" tick={{ fontSize: 10, fill: '#6B7280' }} />
                            <Line type="monotone" dataKey="live" stroke="#E63946" strokeWidth={2} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="sig" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800"><strong>Action:</strong> 5 inactive clients perlu follow-up untuk re-engagement</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Download Button */}
              <div className="flex justify-center pt-4">
                <Button className="bg-gradient-to-r from-[#E63946] to-[#F97316] hover:from-[#DC2626] hover:to-[#EA580C] text-white px-8" onClick={() => {
                  const report = { channel: ib?.name || 'Channel', period: summaryPeriod === 'weekly' ? `Week ${summaryWeekIndex + 1}` : format(new Date(), 'MMMM yyyy'), department: summaryDepartment, generatedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss') };
                  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `performance_report_${format(new Date(), 'yyyy-MM-dd')}.json`;
                  a.click();
                }}>
                  <Download size={16} className="mr-2" />Download Report
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// =====================================================
// MARKETING TAB
// =====================================================
function MarketingTab({ dateRange, dateFrom, dateTo, dailyData, adjustment, setAdjustment, scenario, setScenario, forecastPeriod, setForecastPeriod, historicalData, isLoading, isFiraInd, usdToMyr = 4.0, kpiTargets = {}, kpiThresholds = null }) {
  const [trendMetric, setTrendMetric] = useState('cpm');
  // Check if we have real data (from localStorage OR API)
  const hasRealData = historicalData && Object.keys(historicalData).length > 0;
  
  // Generate realDailyData for PerformanceTracker - converts daily marketing data to metric format
  const realDailyData = useMemo(() => {
    if (!isFiraInd || !dailyData?.marketing?.length) return null;
    
    // Filter by date range
    const filtered = dailyData.marketing.filter(d => {
      const itemDate = new Date(d.date);
      return itemDate >= dateFrom && itemDate <= dateTo;
    });
    
    if (!filtered.length) return null;
    
    // Convert to format expected by PerformanceTracker
    return filtered.map(d => ({
      date: d.date,
      spend: Object.values(d.spend || {}).reduce((s, v) => s + (v || 0), 0),
      lpv: d.lpv || 0,
      cta: d.cta || 0,
      clicks: d.cta || 0, // CTA is used as clicks
      subscribers: (d.subsTG || 0) + (d.subsBot || 0),
      leads: d.leads || 0,
      closed: d.closed || 0,
    }));
  }, [isFiraInd, dailyData, dateFrom, dateTo]);
  
  // Get multiplier based on date range (for mock data)
  const getMultiplier = () => {
    const multipliers = {
      'today': 0.05, 'yesterday': 0.04, 'this-week': 0.3, 
      'last-week': 0.25, 'this-month': 1, 'last-month': 0.9, 'full': 3
    };
    return multipliers[dateRange] || 1;
  };
  const m = getMultiplier();

  // Scorecard metrics - ALWAYS show New/Prospect/VIP Leads (M) - show 0 or - if no data
  // Aggregate leadsM breakdown from dailyData (marketing source)
  const marketingLeadsStats = (() => {
    // Use real data from dailyData.sales for ALL IBs (not just FIRA IND)
    if (dailyData?.sales?.length > 0) {
      const filteredSales = dailyData.sales.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      let leadsM_new = 0, leadsM_prospect = 0, leadsM_vip = 0;
      
      filteredSales.forEach(d => {
        const leadsM = d.leadsM || {};
        leadsM_new += (leadsM.new || 0);
        leadsM_prospect += (leadsM.prospect || 0);
        leadsM_vip += (leadsM.vip || 0);
      });
      return { 
        leadsM_new,
        leadsM_prospect,
        leadsM_vip,
        leadsM_total: leadsM_new + leadsM_prospect + leadsM_vip
      };
    }
    return { 
      leadsM_new: 0,
      leadsM_prospect: 0,
      leadsM_vip: 0,
      leadsM_total: historicalData?.total_leads_m || 0
    };
  })();

  const scorecardMetrics = hasRealData ? [
    { label: 'Total Spend', value: `RM ${Math.round(historicalData.total_ad_spent || 0).toLocaleString()}`, icon: DollarSign, color: '#E63946' },
    { label: 'Impressions', value: Math.round(historicalData.total_impressions || 0).toLocaleString(), icon: Eye, color: '#3B82F6' },
    { label: 'Clicks', value: Math.round(historicalData.total_clicks || 0).toLocaleString(), icon: MousePointer, color: '#F97316' },
    { label: 'LPV', value: Math.round(historicalData.total_lpv || 0).toLocaleString(), icon: Eye, color: '#8B5CF6' },
    { label: 'CTA', value: Math.round(historicalData.total_cta || 0).toLocaleString(), icon: MousePointer, color: '#10B981' },
    { label: 'Subs FT', value: Math.round(historicalData.total_subs_free_trial || 0).toLocaleString(), icon: Users, color: '#EC4899' },
    { label: 'Subs TG', value: Math.round(historicalData.total_subs_tg || 0).toLocaleString(), icon: Users, color: '#3B82F6' },
    { label: 'Subs Bot', value: Math.round(historicalData.total_subs_bot || 0).toLocaleString(), icon: Users, color: '#F59E0B' },
    { label: 'Total Subs', value: Math.round(historicalData.total_subscribers || 0).toLocaleString(), icon: Users, color: '#10B981' },
    { label: 'CPS', value: (historicalData.total_subscribers || 0) > 0 ? `RM ${(historicalData.total_ad_spent / historicalData.total_subscribers).toFixed(2)}` : '-', icon: DollarSign, color: '#F97316' },
    { label: 'CPS (FT)', value: (historicalData.total_subs_free_trial || 0) > 0 ? `RM ${(historicalData.total_ad_spent / historicalData.total_subs_free_trial).toFixed(2)}` : '-', icon: DollarSign, color: '#EC4899' },
    { label: 'Closed', value: Math.round(historicalData.total_closing || 0).toLocaleString(), icon: Zap, color: '#10B981' },
    { label: 'Commission', value: `RM ${Math.round(historicalData.total_commission || 0).toLocaleString()}`, icon: DollarSign, color: '#F59E0B' },
    { label: 'ROI', value: historicalData.total_ad_spent > 0 ? `${((historicalData.total_commission || 0) / historicalData.total_ad_spent * 100).toFixed(1)}%` : '-', icon: TrendingUp, color: '#10B981' },
  ] : isFiraInd ? [
    // FIRA IND without data - show empty but keep structure
    { label: 'Total Spend', value: '-', icon: DollarSign, color: '#6B7280' },
    { label: 'Impressions', value: '-', icon: Eye, color: '#6B7280' },
    { label: 'Clicks', value: '-', icon: MousePointer, color: '#6B7280' },
    { label: 'LPV', value: '-', icon: Eye, color: '#6B7280' },
    { label: 'CTA', value: '-', icon: MousePointer, color: '#6B7280' },
    { label: 'Subs FT', value: '-', icon: Users, color: '#6B7280' },
    { label: 'Subs TG', value: '-', icon: Users, color: '#6B7280' },
    { label: 'Subs Bot', value: '-', icon: Users, color: '#6B7280' },
    { label: 'Total Subs', value: '-', icon: Users, color: '#6B7280' },
    { label: 'Closed', value: '-', icon: Zap, color: '#6B7280' },
    { label: 'Commission', value: '-', icon: DollarSign, color: '#6B7280' },
    { label: 'ROI', value: '-', icon: TrendingUp, color: '#6B7280' },
  ] : [
    { label: 'Total Spend', value: `RM ${Math.round(9435 * m).toLocaleString()}`, icon: DollarSign, color: '#E63946' },
    { label: 'Impressions', value: Math.round(616284 * m).toLocaleString(), icon: Eye, color: '#3B82F6' },
    { label: 'Clicks', value: Math.round(34630 * m).toLocaleString(), icon: MousePointer, color: '#F97316' },
    { label: 'LPV', value: Math.round(25935 * m).toLocaleString(), icon: Eye, color: '#8B5CF6' },
    { label: 'CTA', value: Math.round(4233 * m).toLocaleString(), icon: MousePointer, color: '#10B981' },
    { label: 'Subs TG', value: Math.round(80 * m).toLocaleString(), icon: Users, color: '#3B82F6' },
    { label: 'Subs Bot', value: Math.round(20 * m).toLocaleString(), icon: Users, color: '#F59E0B' },
    { label: 'Total Subs', value: Math.round(100 * m).toLocaleString(), icon: Users, color: '#10B981' },
    { label: 'Closed', value: Math.round(44 * m).toLocaleString(), icon: Zap, color: '#10B981' },
    { label: 'Commission', value: `RM ${Math.round(6200 * m * usdToMyr).toLocaleString()}`, icon: DollarSign, color: '#F59E0B' },
    { label: 'ROI', value: `${((6200 * usdToMyr / 9435) * 100).toFixed(1)}%`, icon: TrendingUp, color: '#10B981' },
  ];

  // Funnel data - FIXED ORDER: Impressions > Clicks > LPV > CTA Button > Subscribers > Leads > Closed
  // CONVERSION RATE FORMULA: (next_step / current_step) × 100
  const funnelData = hasRealData ? [
    { stage: 'Impressions', value: Math.round(historicalData.total_impressions || 0), convRate: historicalData.total_impressions > 0 && historicalData.total_clicks > 0 ? ((historicalData.total_clicks / historicalData.total_impressions) * 100).toFixed(2) : 0 },
    { stage: 'Clicks', value: Math.round(historicalData.total_clicks || 0), convRate: historicalData.total_clicks > 0 && historicalData.total_lpv > 0 ? ((historicalData.total_lpv / historicalData.total_clicks) * 100).toFixed(2) : 0 },
    { stage: 'Landing Page View', value: Math.round(historicalData.total_lpv || 0), convRate: historicalData.total_lpv > 0 && historicalData.total_cta > 0 ? ((historicalData.total_cta / historicalData.total_lpv) * 100).toFixed(2) : 0 },
    { stage: 'CTA Button', value: Math.round(historicalData.total_cta || 0), convRate: historicalData.total_cta > 0 && historicalData.total_subscribers > 0 ? ((historicalData.total_subscribers / historicalData.total_cta) * 100).toFixed(2) : 0 },
    { stage: 'Subscribers', value: Math.round(historicalData.total_subscribers || 0), convRate: historicalData.total_subscribers > 0 && historicalData.total_leads > 0 ? ((historicalData.total_leads / historicalData.total_subscribers) * 100).toFixed(2) : 0 },
    { stage: 'Leads', value: Math.round(historicalData.total_leads || 0), convRate: historicalData.total_leads > 0 && historicalData.total_closing > 0 ? ((historicalData.total_closing / historicalData.total_leads) * 100).toFixed(2) : 0 },
    { stage: 'Closed', value: Math.round(historicalData.total_closing || 0), convRate: null },
  ] : isFiraInd ? [
    // FIRA IND without data - keep structure, show 0
    { stage: 'Impressions', value: 0, convRate: 0 },
    { stage: 'Clicks', value: 0, convRate: 0 },
    { stage: 'Landing Page View', value: 0, convRate: 0 },
    { stage: 'CTA Button', value: 0, convRate: 0 },
    { stage: 'Subscribers', value: 0, convRate: 0 },
    { stage: 'Leads', value: 0, convRate: 0 },
    { stage: 'Closed', value: 0, convRate: null },
  ] : [
     // Mock data with correct conversion rate calculation
    { stage: 'Impressions', value: Math.round(616284 * m), convRate: ((34630 / 616284) * 100).toFixed(2) }, // Clicks / Impressions
    { stage: 'Clicks', value: Math.round(34630 * m), convRate: ((25935 / 34630) * 100).toFixed(2) }, // LPV / Clicks
    { stage: 'Landing Page View', value: Math.round(25935 * m), convRate: ((4233 / 25935) * 100).toFixed(2) }, // CTA / LPV
    { stage: 'CTA Button', value: Math.round(4233 * m), convRate: ((100 / 4233) * 100).toFixed(2) }, // Subs / CTA
    { stage: 'Subscribers', value: Math.round(100 * m), convRate: ((99 / 100) * 100).toFixed(2) }, // Leads / Subs
    { stage: 'Leads', value: Math.round(99 * m), convRate: ((44 / 99) * 100).toFixed(2) }, // Closed / Leads
    { stage: 'Closed', value: Math.round(44 * m), convRate: null },
  ];

  // Conversion rates detail - FIXED ORDER: Clicks Conv > LPV Conv > CTA Conv > Subs Conv > Leads Conv > Closing Rate
  // FORMULA: (next_step / current_step) × 100
  const conversionRatesData = hasRealData ? [
    { from: 'Impressions', to: 'Clicks', name: 'Clicks Conversion', rate: historicalData.total_impressions > 0 ? parseFloat(((historicalData.total_clicks / historicalData.total_impressions) * 100).toFixed(2)) : 0 },
    { from: 'Clicks', to: 'LPV', name: 'LPV Conversion', rate: historicalData.total_clicks > 0 ? parseFloat(((historicalData.total_lpv / historicalData.total_clicks) * 100).toFixed(2)) : 0 },
    { from: 'LPV', to: 'CTA', name: 'CTA Conversion', rate: historicalData.total_lpv > 0 ? parseFloat(((historicalData.total_cta / historicalData.total_lpv) * 100).toFixed(2)) : 0 },
    { from: 'CTA', to: 'Subscribers', name: 'Subs Conversion', rate: historicalData.total_cta > 0 ? parseFloat(((historicalData.total_subscribers / historicalData.total_cta) * 100).toFixed(2)) : 0 },
    { from: 'Subscribers', to: 'Leads', name: 'Leads Conversion', rate: historicalData.total_subscribers > 0 ? parseFloat(((historicalData.total_leads / historicalData.total_subscribers) * 100).toFixed(2)) : 0 },
    { from: 'Leads', to: 'Closed', name: 'Closing Rate', rate: historicalData.total_leads > 0 ? parseFloat(((historicalData.total_closing / historicalData.total_leads) * 100).toFixed(2)) : 0 },
  ] : isFiraInd ? [
    // FIRA IND without data - keep structure, show 0
    { from: 'Impressions', to: 'Clicks', name: 'Clicks Conversion', rate: 0 },
    { from: 'Clicks', to: 'LPV', name: 'LPV Conversion', rate: 0 },
    { from: 'LPV', to: 'CTA', name: 'CTA Conversion', rate: 0 },
    { from: 'CTA', to: 'Subscribers', name: 'Subs Conversion', rate: 0 },
    { from: 'Subscribers', to: 'Leads', name: 'Leads Conversion', rate: 0 },
    { from: 'Leads', to: 'Closed', name: 'Closing Rate', rate: 0 },
  ] : [
    // Mock data with correct calculation: (next / current) × 100
    { from: 'Impressions', to: 'Clicks', name: 'Clicks Conversion', rate: parseFloat(((34630 / 616284) * 100).toFixed(2)) }, // 5.62%
    { from: 'Clicks', to: 'LPV', name: 'LPV Conversion', rate: parseFloat(((25935 / 34630) * 100).toFixed(2)) }, // 74.89%
    { from: 'LPV', to: 'CTA', name: 'CTA Conversion', rate: parseFloat(((4233 / 25935) * 100).toFixed(2)) }, // 16.32%
    { from: 'CTA', to: 'Subscribers', name: 'Subs Conversion', rate: parseFloat(((100 / 4233) * 100).toFixed(2)) }, // 2.36%
    { from: 'Subscribers', to: 'Leads', name: 'Leads Conversion', rate: parseFloat(((99 / 100) * 100).toFixed(2)) }, // 99%
    { from: 'Leads', to: 'Closed', name: 'Closing Rate', rate: parseFloat(((44 / 99) * 100).toFixed(2)) }, // 44.44%
  ];

  // Trend data - Use daily data from localStorage filtered by date range
  const trendData = (() => {
    if (isFiraInd && dailyData?.marketing?.length > 0) {
      // Filter by date range
      const filteredMarketing = dailyData.marketing.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      
      if (!filteredMarketing.length) return [];
      
      return filteredMarketing.map(d => ({
        date: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
        spend: Math.round(Object.values(d.spend || {}).reduce((s, v) => s + (v || 0), 0)),
        subscribers: Math.round((d.subsTG || 0) + (d.subsBot || 0)),
        leads: Math.round(d.leads || 0),
        closed: Math.round(d.closed || 0),
      }));
    }
    
    if (isFiraInd) return [];
    
    return [
      { date: '01/01', spend: Math.round(1200 * m), subscribers: Math.round(15 * m), leads: Math.round(12 * m), closed: Math.round(3 * m) },
      { date: '02/01', spend: Math.round(1350 * m), subscribers: Math.round(18 * m), leads: Math.round(15 * m), closed: Math.round(4 * m) },
      { date: '03/01', spend: Math.round(1180 * m), subscribers: Math.round(12 * m), leads: Math.round(10 * m), closed: Math.round(3 * m) },
      { date: '04/01', spend: Math.round(1420 * m), subscribers: Math.round(20 * m), leads: Math.round(18 * m), closed: Math.round(6 * m) },
      { date: '05/01', spend: Math.round(1550 * m), subscribers: Math.round(22 * m), leads: Math.round(20 * m), closed: Math.round(8 * m) },
      { date: '06/01', spend: Math.round(1380 * m), subscribers: Math.round(16 * m), leads: Math.round(14 * m), closed: Math.round(5 * m) },
      { date: '07/01', spend: Math.round(1480 * m), subscribers: Math.round(19 * m), leads: Math.round(16 * m), closed: Math.round(7 * m) },
    ];
  })();

  // Ads performance - Calculate from data with proper formulas
  const kpiMkt = kpiThresholds?.marketing || {};
  const calcKpiMetric = (rawVal, thresholdKey, dept = 'marketing') => {
    const th = (dept === 'marketing' ? kpiMkt : dept === 'sales' ? kpiThresholds?.sales : kpiThresholds?.operation) || {};
    const t = th[thresholdKey];
    if (!t || !rawVal) return { color: '#6B7280', status: 'No Data' };
    return { color: getPerformanceColor(rawVal, t, t.lower_better), status: getPerformanceStatus(rawVal, t, t.lower_better) };
  };

  const adsMetrics = hasRealData ? (() => {
    const d = historicalData;
    const cpm = d.total_impressions > 0 ? (d.total_ad_spent / d.total_impressions) * 1000 : 0;
    const ctr = d.total_impressions > 0 ? (d.total_clicks / d.total_impressions) * 100 : 0;
    const cplc = d.total_clicks > 0 ? d.total_ad_spent / d.total_clicks : 0;
    const cplpv = d.total_lpv > 0 ? d.total_ad_spent / d.total_lpv : 0;
    const cpb = (d.total_cta || d.total_clicks) > 0 ? d.total_ad_spent / (d.total_cta || d.total_clicks) : 0;
    const cps = d.total_subscribers > 0 ? d.total_ad_spent / d.total_subscribers : 0;
    const cpsFt = (d.total_subs_free_trial || 0) > 0 ? d.total_ad_spent / d.total_subs_free_trial : 0;
    const cpl = d.total_leads > 0 ? d.total_ad_spent / d.total_leads : 0;
    const cpcClient = d.total_closing > 0 ? d.total_ad_spent / d.total_closing : 0;
    return [
      { label: 'CPM', value: cpm > 0 ? `RM ${cpm.toFixed(2)}` : '-', rawVal: cpm, ...calcKpiMetric(cpm, 'CPM'), formula: 'Spend ÷ Impressions × 1000', hasData: cpm > 0 },
      { label: 'CTR', value: ctr > 0 ? `${ctr.toFixed(2)}%` : '-', rawVal: ctr, ...calcKpiMetric(ctr, 'CTR'), formula: 'Clicks ÷ Impressions × 100', hasData: ctr > 0 },
      { label: 'CPLC', value: cplc > 0 ? `RM ${cplc.toFixed(2)}` : '-', rawVal: cplc, ...calcKpiMetric(cplc, 'CPLC'), formula: 'Spend ÷ Clicks', hasData: cplc > 0 },
      { label: 'CPLPV', value: cplpv > 0 ? `RM ${cplpv.toFixed(2)}` : '-', rawVal: cplpv, ...calcKpiMetric(cplpv, 'CPLPV'), formula: 'Spend ÷ LPV', hasData: cplpv > 0 },
      { label: 'CPB', value: cpb > 0 ? `RM ${cpb.toFixed(2)}` : '-', rawVal: cpb, ...calcKpiMetric(cpb, 'CPB'), formula: 'Spend ÷ CTA Clicks', hasData: cpb > 0 },
      { label: 'CPS', value: cps > 0 ? `RM ${cps.toFixed(2)}` : '-', rawVal: cps, ...calcKpiMetric(cps, 'CPS'), formula: 'Spend ÷ Subscribers', hasData: cps > 0 },
      { label: 'CPS (FT)', value: cpsFt > 0 ? `RM ${cpsFt.toFixed(2)}` : '-', rawVal: cpsFt, ...calcKpiMetric(cpsFt, 'CPS_FT'), formula: 'Spend ÷ Subs Free Trial', hasData: cpsFt > 0 },
      { label: 'CPL', value: cpl > 0 ? `RM ${cpl.toFixed(2)}` : '-', rawVal: cpl, ...calcKpiMetric(cpl, 'CPL'), formula: 'Spend ÷ Leads', hasData: cpl > 0 },
      { label: 'CPC (Client)', value: cpcClient > 0 ? `RM ${cpcClient.toFixed(2)}` : '-', rawVal: cpcClient, ...calcKpiMetric(cpcClient, 'CPC_CLIENT'), formula: 'Spend ÷ Closing', hasData: cpcClient > 0 },
    ];
  })() : [
    { label: 'CPM', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ Impressions × 1000', hasData: false },
    { label: 'CTR', value: '-', color: '#6B7280', status: 'No Data', formula: 'Clicks ÷ Impressions × 100', hasData: false },
    { label: 'CPLC', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ Clicks', hasData: false },
    { label: 'CPLPV', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ LPV', hasData: false },
    { label: 'CPB', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ CTA Clicks', hasData: false },
    { label: 'CPS', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ Subscribers', hasData: false },
    { label: 'CPS (FT)', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ Subs Free Trial', hasData: false },
    { label: 'CPL', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ Leads', hasData: false },
    { label: 'CPC (Client)', value: '-', color: '#6B7280', status: 'No Data', formula: 'Spend ÷ Closing', hasData: false },
  ];

  const maxValue = funnelData[0]?.value || 1;

  // Performance tier with 4 levels: Excellent, Good, Average, Poor
  const getConversionStatus = (rate) => {
    if (rate >= 50) return { status: 'excellent', color: '#3B82F6' };
    if (rate >= 30) return { status: 'good', color: '#10B981' };
    if (rate >= 15) return { status: 'average', color: '#F59E0B' };
    return { status: 'poor', color: '#E63946' };
  };

  return (
    <div className="space-y-6">
      {/* PERFORMANCE PROJECTIONS */}
      <DepartmentProjections 
        department="marketing" 
        adjustment={adjustment} 
        setAdjustment={setAdjustment}
        scenario={scenario}
        setScenario={setScenario}
        forecastPeriod={forecastPeriod}
        setForecastPeriod={setForecastPeriod}
        historicalData={historicalData}
        isFiraInd={isFiraInd}
      />
      {/* SCORECARD - Overall Department Metrics (2 rows if > 6 metrics) */}
      <SectionCard>
        <SectionHeader icon={BarChart3} title="Marketing Scorecard" subtitle="Key Department Metrics" />
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {scorecardMetrics.map((metric, idx) => {
              const Icon = metric.icon;
              return (
                <div 
                  key={idx} 
                  className="p-3 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-center"
                >
                  <Icon size={18} style={{ color: metric.color }} className="mx-auto mb-1.5" />
                  <p className="text-[9px] uppercase tracking-wider text-[#9CA3AF] mb-1">{metric.label}</p>
                  <p className="text-lg font-mono font-bold text-white">{metric.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* Row 1: Pyramid Funnel + Conversion Rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Pyramid Funnel - INVERTED PYRAMID SHAPE */}
        <SectionCard>
          <SectionHeader icon={TrendingDown} title="Conversion Funnel (Pyramid)" subtitle="Impressions to Closed" />
          <div className="p-6">
            <div className="space-y-2">
              {funnelData.map((item, idx) => {
                // Calculate width - starts at 100%, decreases each level
                const widthPercent = 100 - (idx * 12); // 100%, 88%, 76%, 64%, 52%, 40%, 28%
                const convRate = item.convRate;
                const color = convRate === null ? '#10B981' : getPerformanceColor(convRate, { good: 50, average: 20 });
                const nextItem = funnelData[idx + 1];
                
                return (
                  <div key={item.stage} className="flex flex-col items-center">
                    <div 
                      className="relative h-12 rounded-lg flex items-center justify-between px-4 transition-all"
                      style={{ 
                        width: `${widthPercent}%`,
                        backgroundColor: color,
                        minWidth: '150px'
                      }}
                    >
                      <span className="text-white font-medium text-sm truncate">{item.stage}</span>
                      <span className="text-white font-mono font-bold">{item.value.toLocaleString()}</span>
                    </div>
                    {convRate !== null && nextItem && (
                      <div className="flex items-center gap-1 my-1">
                        <ArrowDown size={12} className="text-[#9CA3AF]" />
                        <span 
                          className="text-xs font-medium"
                          style={{ color: getPerformanceColor(convRate, { good: 50, average: 20 }) }}
                        >
                          {convRate}% → {nextItem.stage.split(' ')[0]}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>

        {/* Conversion Rates Detail - Uses real data for FIRA IND 2025 */}
        <SectionCard>
          <SectionHeader icon={BarChart3} title="Conversion Rates Detail" subtitle="Stage-by-Stage Analysis" action={<KpiHelpButton title="Conversion Rates KPI Guide" customKpis={CONVERSION_RATE_KPIS} />} />
          <div className="p-6 space-y-3">
            {conversionRatesData.map((item, idx) => {
              const { status, color } = getConversionStatus(item.rate);
              const showNoData = isFiraInd && !hasRealData;
              
              return (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-4 rounded-lg border"
                  style={{ backgroundColor: showNoData ? 'rgba(107, 114, 128, 0.1)' : `${color}10`, borderColor: showNoData ? 'rgba(107, 114, 128, 0.3)' : `${color}30` }}
                >
                  <div>
                    <p className="text-sm font-medium text-white">{item.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{item.from} → {item.to}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {showNoData ? (
                      <p className="text-xl font-mono font-bold text-[#6B7280]">-</p>
                    ) : (
                      <>
                        <p className="text-2xl font-mono font-bold" style={{ color }}>{item.rate}%</p>
                        <Badge style={{ backgroundColor: `${color}20`, color }} className="border-none text-[10px]">
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Row 2: Trend Graph */}
      <SectionCard>
        <SectionHeader icon={TrendingUp} title="Trend Graph" subtitle="Spend, Subscribers, Leads & Closed" />
        <div className="p-6">
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis yAxisId="left" stroke="#E63946" />
                <YAxis yAxisId="right" orientation="right" stroke="#3B82F6" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#E63946" strokeWidth={3} name="Spend (RM)" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="subscribers" stroke="#3B82F6" strokeWidth={2} name="Subscribers" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="leads" stroke="#F97316" strokeWidth={2} name="Leads" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="closed" stroke="#10B981" strokeWidth={2} name="Closed" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center">
              <div className="text-center">
                <BarChart3 size={48} className="text-[#6B7280] mx-auto mb-3" />
                <p className="text-[#9CA3AF]">No trend data available for selected period</p>
                <p className="text-xs text-[#6B7280] mt-1">Select a 2025 date range to view historical trends</p>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Row 3: Trend Graph - Ads Performance (Daily) */}
      <SectionCard>
        <SectionHeader icon={TrendingUp} title="Trend Graph: Ads Performance" subtitle="Daily cost-per-metric trends" />
        <div className="p-4 sm:p-6">
          <TrendGraphAds dailyData={dailyData} dateFrom={dateFrom} dateTo={dateTo} />
        </div>
      </SectionCard>

      {/* Row 4: Ads Performance */}
      <SectionCard>
        <SectionHeader icon={DollarSign} title="Ads Performance" subtitle="Cost Per Metrics (Calculated from data)" action={<KpiHelpButton title="Ads Performance KPI Guide" metrics={['CPM','CTR','CPLC','CPLPV','CPB','CPS','CPS_FT','CPL','CPC_CLIENT']} />} />
        <div className="p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {adsMetrics.map((metric, idx) => {
              const color = metric.color || (metric.hasData ? getPerformanceColor(metric.rate || 0, { excellent: 90, good: 70, average: 40 }) : '#6B7280');
              const status = metric.status || (metric.hasData ? getPerformanceStatus(metric.rate || 0, { excellent: 90, good: 70, average: 40 }) : 'No Data');
              
              return (
                <div 
                  key={idx} 
                  className="p-4 rounded-lg border group relative"
                  style={{ backgroundColor: `${color}10`, borderColor: `${color}30` }}
                >
                  <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">{metric.label}</p>
                  <p className="text-xl font-mono font-bold" style={{ color }}>{metric.value}</p>
                  <p className="text-xs mt-1 capitalize" style={{ color }}>{status}</p>
                  {/* Formula tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1F2937] text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                    <span className="text-[#9CA3AF]">Formula:</span> {metric.formula}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1F2937]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* PERFORMANCE TRACKER - Marketing (Using Reusable Component) */}
      {hasRealData ? (
        <PerformanceTracker 
          metrics={[
            { key: 'spend', label: 'Total Spend', current: Math.round(historicalData.total_ad_spent || 0), target: kpiTargets.marketing?.totalSpend || 0, dailyTarget: (kpiTargets.marketing?.totalSpend || 0) / 30, icon: DollarSign, color: '#E63946', prefix: 'RM ' },
            { key: 'lpv', label: 'LPV', current: Math.round(historicalData.total_lpv || 0), target: kpiTargets.marketing?.lpv || 0, dailyTarget: (kpiTargets.marketing?.lpv || 0) / 30, icon: Eye, color: '#3B82F6' },
            { key: 'clicks', label: 'CTA Clicks', current: Math.round(historicalData.total_clicks || 0), target: kpiTargets.marketing?.ctaButton || 0, dailyTarget: (kpiTargets.marketing?.ctaButton || 0) / 30, icon: MousePointer, color: '#F97316' },
            { key: 'subscribers', label: 'Subscribers', current: Math.round(historicalData.total_subscribers || 0), target: kpiTargets.sales?.greeting || 0, dailyTarget: (kpiTargets.sales?.greeting || 0) / 30, icon: Users, color: '#10B981' },
            { key: 'leads', label: 'Leads', current: Math.round(historicalData.total_leads || 0), target: kpiTargets.sales?.registration || 0, dailyTarget: (kpiTargets.sales?.registration || 0) / 30, icon: Target, color: '#F59E0B' },
            { key: 'closed', label: 'Closed', current: Math.round(historicalData.total_closing || 0), target: kpiTargets.sales?.broker || 0, dailyTarget: (kpiTargets.sales?.broker || 0) / 30, icon: Zap, color: '#10B981' },
          ]}
          title="Performance Tracker"
          subtitle={`Marketing Data • ${dateRange?.replace('2025-', '').toUpperCase() || 'MTD'}`}
          department="marketing"
          realDailyData={realDailyData}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      ) : (
        <SectionCard>
          <SectionHeader icon={BarChart3} title="Performance Tracker" subtitle="Marketing Monthly Target Progress" />
          <div className="p-6 text-center text-[#6B7280]">
            <p className="text-sm">Tiada data untuk tempoh ini. Pilih tarikh 2025 untuk melihat data sebenar.</p>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// =====================================================
// SALES TAB
// =====================================================
function SalesTab({ dateRange, dateFrom, dateTo, dailyData, adjustment, setAdjustment, scenario, setScenario, forecastPeriod, setForecastPeriod, historicalData, isLoading, isFiraInd, usdToMyr = 4.0, kpiTargets = {}, kpiThresholds = null }) {
  // Check if we have real data (from localStorage OR API)
  const hasRealData = historicalData && Object.keys(historicalData).length > 0;
  
  // Calculate days in current month
  const currentDate = new Date();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  
  // Generate realDailyData for PerformanceTracker - converts daily sales data to metric format
  const realDailyData = useMemo(() => {
    if (!isFiraInd || !dailyData?.sales?.length) return null;
    
    // Filter by date range
    const filtered = dailyData.sales.filter(d => {
      const itemDate = new Date(d.date);
      return itemDate >= dateFrom && itemDate <= dateTo;
    });
    
    if (!filtered.length) return null;
    
    // Helper function to sum broker values
    const sumBrokerValues = (d, field) => {
      if (d.brokers) {
        return Object.values(d.brokers).reduce((sum, b) => sum + (b?.[field] || 0), 0);
      }
      return (d.brokerA?.[field] || 0) + (d.brokerB?.[field] || 0) + (d.brokerC?.[field] || 0);
    };
    
    // Convert to format expected by PerformanceTracker
    return filtered.map(d => ({
      date: d.date,
      leads: (d.leadsM?.new || 0) + (d.leadsM?.prospect || 0) + (d.leadsM?.vip || 0) +
             (d.leadsS?.new || 0) + (d.leadsS?.prospect || 0) + (d.leadsS?.vip || 0) +
             (d.leadsIB?.new || 0) + (d.leadsIB?.prospect || 0) + (d.leadsIB?.vip || 0),
      closed: d.closed || 0,
      deposit: d.total_deposit_usd !== undefined ? d.total_deposit_usd : sumBrokerValues(d, 'deposit_usd') || sumBrokerValues(d, 'deposit'),
      commission: d.total_commission_usd !== undefined ? d.total_commission_usd : sumBrokerValues(d, 'commission_usd') || sumBrokerValues(d, 'commission'),
      activeClient: sumBrokerValues(d, 'active'),
    }));
  }, [isFiraInd, dailyData, dateFrom, dateTo]);
  
  const getMultiplier = () => {
    const multipliers = { 'today': 0.05, 'yesterday': 0.04, 'this-week': 0.3, 'last-week': 0.25, 'this-month': 1, 'last-month': 0.9, 'full': 3 };
    return multipliers[dateRange] || 1;
  };
  const m = getMultiplier();

  // Scorecard metrics for Sales - ALWAYS show New/Prospect/VIP Leads
  // Calculate newClosed and vipClosed from filtered sales data
  // Also calculate leadsS breakdown (new, prospect, vip)
  const salesStats = (() => {
    if (dailyData?.sales?.length > 0) {
      const filteredSales = dailyData.sales.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      let newClosed = 0, vipClosed = 0;
      let leads_new = 0, leads_prospect = 0, leads_vip = 0;
      
      filteredSales.forEach(d => {
        newClosed += (d.newClosed || d.closed || 0);
        vipClosed += (d.vipClosed || 0);
        // Aggregate ALL leads sources (M + S + IB combined)
        for (const key of ['leadsM', 'leadsS', 'leadsIB']) {
          const v = d[key];
          if (typeof v === 'object' && v !== null) {
            leads_new += (v.new || 0);
            leads_prospect += (v.prospect || 0);
            leads_vip += (v.vip || 0);
          } else if (typeof v === 'number') {
            leads_new += v;
          }
        }
      });
      return { 
        newClosed, 
        vipClosed, 
        totalClosed: newClosed + vipClosed,
        leadsS_new: leads_new,
        leadsS_prospect: leads_prospect,
        leadsS_vip: leads_vip,
        leadsS_total: leads_new + leads_prospect + leads_vip
      };
    }
    return { 
      newClosed: 0, 
      vipClosed: 0, 
      totalClosed: historicalData?.total_closing || 0,
      leadsS_new: 0,
      leadsS_prospect: 0,
      leadsS_vip: 0,
      leadsS_total: historicalData?.total_leads_s || historicalData?.total_leads || 0
    };
  })();
  
  const scorecardMetrics = hasRealData ? [
    { label: 'New Leads', value: salesStats.leadsS_new > 0 ? Math.round(salesStats.leadsS_new).toLocaleString() : '-', icon: Target, color: '#10B981' },
    { label: 'Prospect Leads', value: salesStats.leadsS_prospect > 0 ? Math.round(salesStats.leadsS_prospect).toLocaleString() : '-', icon: Target, color: '#F59E0B' },
    { label: 'VIP Leads', value: salesStats.leadsS_vip > 0 ? Math.round(salesStats.leadsS_vip).toLocaleString() : '-', icon: Target, color: '#8B5CF6' },
    { label: 'Total Leads', value: Math.round(salesStats.leadsS_total || historicalData.total_leads_s || 0).toLocaleString(), icon: Target, color: '#3B82F6' },
    { label: 'New Closed', value: Math.round(salesStats.newClosed).toLocaleString(), icon: Zap, color: '#3B82F6' },
    { label: 'Returning Closed', value: Math.round(salesStats.vipClosed).toLocaleString(), icon: Zap, color: '#8B5CF6' },
    { label: 'Total Closed', value: Math.round(salesStats.totalClosed).toLocaleString(), icon: Zap, color: '#10B981' },
    { label: 'Deposit', value: `$ ${Math.round(historicalData.total_deposit || 0).toLocaleString()}`, icon: DollarSign, color: '#F59E0B' },
    { label: 'Commission', value: `RM ${Math.round(historicalData.total_commission || 0).toLocaleString()}`, icon: DollarSign, color: '#8B5CF6' },
    { label: 'Active Client', value: Math.round(historicalData.total_active_client || 0).toLocaleString(), icon: Users, color: '#F97316' },
    { label: 'Closing Rate', value: (salesStats.leadsS_total || historicalData.total_leads_s || 0) > 0 ? `${((salesStats.totalClosed / (salesStats.leadsS_total || historicalData.total_leads_s)) * 100).toFixed(1)}%` : '-', icon: TrendingUp, color: '#E63946' },
  ] : isFiraInd ? [
    { label: 'New Leads', value: '-', icon: Target, color: '#6B7280' },
    { label: 'Prospect Leads', value: '-', icon: Target, color: '#6B7280' },
    { label: 'VIP Leads', value: '-', icon: Target, color: '#6B7280' },
    { label: 'Total Leads', value: '-', icon: Target, color: '#6B7280' },
    { label: 'New Closed', value: '-', icon: Zap, color: '#6B7280' },
    { label: 'Returning Closed', value: '-', icon: Zap, color: '#6B7280' },
    { label: 'Total Closed', value: '-', icon: Zap, color: '#6B7280' },
    { label: 'Deposit', value: '-', icon: DollarSign, color: '#6B7280' },
    { label: 'Commission', value: '-', icon: DollarSign, color: '#6B7280' },
    { label: 'Active Client', value: '-', icon: Users, color: '#6B7280' },
    { label: 'Closing Rate', value: '-', icon: TrendingUp, color: '#6B7280' },
  ] : [
    { label: 'New Leads', value: Math.round(85 * m).toLocaleString(), icon: Target, color: '#10B981' },
    { label: 'Prospect Leads', value: Math.round(52 * m).toLocaleString(), icon: Target, color: '#F59E0B' },
    { label: 'VIP Leads', value: Math.round(32 * m).toLocaleString(), icon: Target, color: '#8B5CF6' },
    { label: 'Total Leads', value: Math.round(169 * m).toLocaleString(), icon: Target, color: '#3B82F6' },
    { label: 'New Closed', value: Math.round(30 * m).toLocaleString(), icon: Zap, color: '#3B82F6' },
    { label: 'Returning Closed', value: Math.round(14 * m).toLocaleString(), icon: Zap, color: '#8B5CF6' },
    { label: 'Total Closed', value: Math.round(44 * m).toLocaleString(), icon: Zap, color: '#10B981' },
    { label: 'Deposit', value: `$${Math.round(11303 * m).toLocaleString()}`, icon: DollarSign, color: '#F59E0B' },
    { label: 'Commission', value: `RM ${Math.round(6200 * m * usdToMyr).toLocaleString()}`, icon: DollarSign, color: '#8B5CF6' },
    { label: 'Active Client', value: Math.round(45 * m).toLocaleString(), icon: Users, color: '#F97316' },
    { label: 'Closing Rate', value: '26%', icon: TrendingUp, color: '#E63946' },
  ];

  // Pipeline data - aggregate from filtered sales data
  const pipelineStages = (() => {
    // Use real data from dailyData.sales for ALL IBs (no mock data)
    if (dailyData?.sales?.length > 0) {
      // Filter by date range
      const filteredSales = dailyData.sales.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      
      // Aggregate pipeline stages
      let totalGreeting = 0, totalTick = 0, totalRegistration = 0, totalBroker = 0, totalOffer = 0, totalOthers = 0;
      let totalLeads = 0, totalClosed = 0;
      
      filteredSales.forEach(d => {
        totalGreeting += (d.greeting || 0);
        totalTick += (d.oneTick || 0);
        totalRegistration += (d.registration || 0);
        totalBroker += (d.broker || 0);
        totalOffer += (d.priceOffer || 0);
        totalOthers += (d.others || 0);
        totalClosed += (d.closed || 0);
        // Calculate total leads from leadsM, leadsS, leadsIB
        const leadsM = d.leadsM || {};
        const leadsS = d.leadsS || {};
        const leadsIB = d.leadsIB || {};
        totalLeads += (leadsM.new || 0) + (leadsM.prospect || 0) + (leadsM.vip || 0) +
                      (leadsS.new || 0) + (leadsS.prospect || 0) + (leadsS.vip || 0) +
                      (leadsIB.new || 0) + (leadsIB.prospect || 0) + (leadsIB.vip || 0);
      });
      
      // Use aggregatedDailyData totals if available (more accurate)
      if (historicalData) {
        totalLeads = historicalData.total_leads || totalLeads;
        totalClosed = historicalData.total_closing || totalClosed;
      }
      
      return {
        leads: Math.round(totalLeads),
        greeting: Math.round(totalGreeting),
        tick: Math.round(totalTick),
        registration: Math.round(totalRegistration),
        broker: Math.round(totalBroker),
        offer: Math.round(totalOffer),
        closed: Math.round(totalClosed),
        // Formula: balanced = leads - closed (open leads still in pipeline, not yet closed)
        balanced: Math.max(0, Math.round(totalLeads) - Math.round(totalClosed)),
      };
    }
    
    // No data available - return zeros (no mock data)
    return {
      leads: 0, greeting: 0, tick: 0, registration: 0, broker: 0, offer: 0, closed: 0, balanced: 0
    };
  })();

  // Daily lead flow - Generate from real data if available
  const dailyLeadFlow = (() => {
    if (isFiraInd && dailyData?.sales?.length > 0) {
      // Filter by date range
      const filteredSales = dailyData.sales.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      
      if (!filteredSales.length) return [];
      
      // Helper function to sum broker values
      const sumBrokers = (d, field) => {
        if (d.brokers) {
          return Object.values(d.brokers).reduce((sum, b) => {
            let v = b?.[field];
            if ((v === undefined || v === null) && field === 'commission') v = b?.commission_usd;
            if ((v === undefined || v === null) && field === 'deposit') v = b?.deposit_usd;
            return sum + (v || 0);
          }, 0);
        }
        return (d.brokerA?.[field] || 0) + (d.brokerB?.[field] || 0) + (d.brokerC?.[field] || 0);
      };
      
      return filteredSales.map(d => ({
        date: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
        leads: Math.round(d.totalLeads || (d.marketingNew || 0) + (d.marketingProspect || 0) + (d.marketingVip || 0) + (d.salesNew || 0) + (d.salesProspect || 0) + (d.salesVip || 0) + (d.ibNew || 0) + (d.ibProspect || 0) + (d.ibVip || 0)),
        closed: Math.round(d.closed || 0),
        deposit: Math.round(d.total_deposit_usd !== undefined ? d.total_deposit_usd : sumBrokers(d, 'deposit_usd') || sumBrokers(d, 'deposit')),
        activeClient: Math.round(sumBrokers(d, 'active')),
        lots: Math.round(sumBrokers(d, 'lots')),
        commission: Math.round(d.total_commission_usd !== undefined ? d.total_commission_usd : sumBrokers(d, 'commission_usd') || sumBrokers(d, 'commission')),
      }));
    }
    
    if (isFiraInd) return [];
    
    return [
      { date: 'Mon', greeting: Math.round(45 * m), tick: Math.round(38 * m), registration: Math.round(30 * m), broker: Math.round(22 * m), offer: Math.round(15 * m), closed: Math.round(8 * m) },
      { date: 'Tue', greeting: Math.round(52 * m), tick: Math.round(44 * m), registration: Math.round(35 * m), broker: Math.round(28 * m), offer: Math.round(20 * m), closed: Math.round(12 * m) },
      { date: 'Wed', greeting: Math.round(38 * m), tick: Math.round(32 * m), registration: Math.round(25 * m), broker: Math.round(18 * m), offer: Math.round(12 * m), closed: Math.round(6 * m) },
      { date: 'Thu', greeting: Math.round(60 * m), tick: Math.round(50 * m), registration: Math.round(40 * m), broker: Math.round(32 * m), offer: Math.round(24 * m), closed: Math.round(14 * m) },
      { date: 'Fri', greeting: Math.round(55 * m), tick: Math.round(46 * m), registration: Math.round(38 * m), broker: Math.round(30 * m), offer: Math.round(22 * m), closed: Math.round(10 * m) },
      { date: 'Sat', greeting: Math.round(30 * m), tick: Math.round(25 * m), registration: Math.round(20 * m), broker: Math.round(15 * m), offer: Math.round(10 * m), closed: Math.round(5 * m) },
      { date: 'Sun', greeting: Math.round(25 * m), tick: Math.round(20 * m), registration: Math.round(16 * m), broker: Math.round(12 * m), offer: Math.round(8 * m), closed: Math.round(4 * m) },
    ];
  })();

  // Sales performance metrics — use KPI thresholds from admin settings
  const kpiSales = kpiThresholds?.sales || {};
  const calcSalesKpi = (rawVal, key) => {
    const t = kpiSales[key];
    if (!t || !rawVal) return { color: '#6B7280', status: 'No Data' };
    return { color: getPerformanceColor(rawVal, t, t.lower_better), status: getPerformanceStatus(rawVal, t, t.lower_better) };
  };
  const salesMetrics = hasRealData ? (() => {
    const d = historicalData;
    const cpcClient = d.total_closing > 0 ? d.total_ad_spent / d.total_closing : 0;
    const cpl = d.total_leads > 0 ? d.total_ad_spent / d.total_leads : 0;
    const avgDep = d.total_closing > 0 ? d.total_deposit / d.total_closing : 0;
    const closingRate = d.total_leads > 0 ? (d.total_closing / d.total_leads) * 100 : 0;
    return [
      { label: 'CPC (Client)', value: cpcClient > 0 ? `RM ${cpcClient.toFixed(2)}` : '-', ...calcSalesKpi(cpcClient, 'CPC_CLIENT_SALES'), formula: 'Ad Spent ÷ Closing', hasData: cpcClient > 0 },
      { label: 'CPL', value: cpl > 0 ? `RM ${cpl.toFixed(2)}` : '-', ...calcSalesKpi(cpl, 'CPL_SALES'), formula: 'Ad Spent ÷ Leads', hasData: cpl > 0 },
      { label: 'Avg Deposit', value: avgDep > 0 ? `$ ${avgDep.toFixed(0)}` : '-', ...calcSalesKpi(avgDep, 'AVG_DEPOSIT'), formula: 'Total Deposit ÷ Closing', hasData: avgDep > 0 },
      { label: 'Renewal Rate', value: '-', color: '#6B7280', status: 'No Data', formula: 'Returning Closed ÷ VIP Leads × 100', hasData: false },
      { label: 'Closing Rate', value: closingRate > 0 ? `${closingRate.toFixed(1)}%` : '-', ...calcSalesKpi(closingRate, 'CLOSING_RATE'), formula: 'Closing ÷ Leads × 100', hasData: closingRate > 0 },
    ];
  })() : [
    { label: 'CPC (Client)', value: '-', color: '#6B7280', status: 'No Data', formula: 'Ad Spent ÷ Closing', hasData: false },
    { label: 'CPL', value: '-', color: '#6B7280', status: 'No Data', formula: 'Ad Spent ÷ Leads', hasData: false },
    { label: 'Avg Deposit', value: '-', color: '#6B7280', status: 'No Data', formula: 'Total Deposit ÷ Closing', hasData: false },
    { label: 'Renewal Rate', value: '-', color: '#6B7280', status: 'No Data', formula: 'Returning Closed ÷ VIP Leads × 100', hasData: false },
    { label: 'Closing Rate', value: '-', color: '#6B7280', status: 'No Data', formula: 'Closing ÷ Leads × 100', hasData: false },
  ];

  const maxPipelineValue = pipelineStages.leads || 1;

  return (
    <div className="space-y-6">
      {/* PERFORMANCE PROJECTIONS */}
      <DepartmentProjections 
        department="sales" 
        adjustment={adjustment} 
        setAdjustment={setAdjustment}
        scenario={scenario}
        setScenario={setScenario}
        forecastPeriod={forecastPeriod}
        setForecastPeriod={setForecastPeriod}
        historicalData={historicalData}
        isFiraInd={isFiraInd}
      />
      {/* SCORECARD */}
      <SectionCard>
        <SectionHeader icon={BarChart3} title="Sales Scorecard" subtitle="Key Department Metrics" />
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {scorecardMetrics.map((metric, idx) => {
              const Icon = metric.icon;
              return (
                <div key={idx} className="p-4 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-center">
                  <Icon size={20} style={{ color: metric.color }} className="mx-auto mb-2" />
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">{metric.label}</p>
                  <p className="text-xl font-mono font-bold text-white">{metric.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* Row 1: Sales Pipeline - Full Width Design with aligned boxes */}
      <SectionCard>
        <SectionHeader icon={Handshake} title="Sales Pipeline" subtitle="Leads → Pipeline → Closed → Balance" />
        <div className="p-6">
          <div className="flex items-stretch gap-4 h-80">
            
            {/* LEADS Box - Higher is better */}
            <div className="w-28 flex flex-col p-3 bg-[#3B82F6]/5 border border-[#3B82F6]/20 rounded-xl">
              <p className="text-center text-sm font-semibold text-[#3B82F6] mb-2">LEADS</p>
              <div className="flex-1 relative bg-[hsl(var(--surface-elevated))] rounded-lg">
                {/* Indicator lines with 0 at bottom */}
                <div className="absolute inset-2 flex flex-col justify-between pointer-events-none">
                  <div className="border-b border-dashed border-[#10B981]/60"><span className="text-[7px] text-[#10B981] bg-[hsl(var(--surface-elevated))] px-1">Excellent</span></div>
                  <div className="border-b border-dashed border-[#3B82F6]/60"><span className="text-[7px] text-[#3B82F6] bg-[hsl(var(--surface-elevated))] px-1">Good</span></div>
                  <div className="border-b border-dashed border-[#F59E0B]/60"><span className="text-[7px] text-[#F59E0B] bg-[hsl(var(--surface-elevated))] px-1">Average</span></div>
                  <div className="border-b border-dashed border-[#E63946]/60"><span className="text-[7px] text-[#E63946] bg-[hsl(var(--surface-elevated))] px-1">Bad</span></div>
                  <div className="border-b border-solid border-[#6B7280]/60"><span className="text-[7px] text-[#6B7280] bg-[hsl(var(--surface-elevated))] px-1">0</span></div>
                </div>
                {/* Bar - color based on which zone it reaches */}
                {(() => {
                  const value = pipelineStages.leads;
                  const maxTarget = Math.max(200, value * 1.2);
                  const percent = Math.min((value / maxTarget) * 100, 95);
                  // Color based on which indicator zone the bar reaches
                  // Excellent = top 20%, Good = 40-60%, Average = 60-80%, Bad = bottom 20%
                  const getColor = () => {
                    if (percent >= 80) return '#10B981'; // Excellent (green)
                    if (percent >= 60) return '#3B82F6'; // Good (blue)
                    if (percent >= 40) return '#F59E0B'; // Average (orange)
                    return '#E63946'; // Bad (red)
                  };
                  return (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8 h-[calc(100%-16px)] flex items-end">
                      <div className="w-full rounded-t transition-all" style={{ height: `${Math.max(percent, 5)}%`, backgroundColor: getColor() }} />
                    </div>
                  );
                })()}
              </div>
              <p className="text-center text-lg font-mono font-bold text-white mt-2">{pipelineStages.leads}</p>
            </div>

            {/* Arrow */}
            <div className="flex items-center"><ChevronRight size={24} className="text-[#6B7280]" /></div>

            {/* PIPELINE Boxes - Each stage in its own aligned box */}
            {[
              { stage: 'Greeting', value: pipelineStages.greeting },
              { stage: '1 Tick', value: pipelineStages.tick },
              { stage: 'Reg', value: pipelineStages.registration },
              { stage: 'Broker', value: pipelineStages.broker },
              { stage: 'Offer', value: pipelineStages.offer },
            ].map((item) => {
              const stuckPercent = pipelineStages.leads > 0 ? (item.value / pipelineStages.leads) * 100 : 0;
              // Height: 0% = 0, 40% stuck = 100% height (full bar)
              const heightPercent = Math.min((stuckPercent / 40) * 100, 95);
              // Color: Lower stuck % is better - Excellent ≤5%, Good ≤15%, Average ≤30%, Bad >30%
              const getColor = () => {
                if (stuckPercent <= 5) return '#10B981'; // Excellent
                if (stuckPercent <= 15) return '#3B82F6'; // Good
                if (stuckPercent <= 30) return '#F59E0B'; // Average
                return '#E63946'; // Bad
              };
              const barColor = getColor();
              return (
                <div key={item.stage} className="w-20 flex flex-col p-3 bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl">
                  <p className="text-center text-xs font-semibold text-[#F59E0B] mb-2 truncate">{item.stage}</p>
                  <div className="flex-1 relative bg-[hsl(var(--surface-elevated))] rounded-lg">
                    {/* Indicator lines - Lower is better, with 0 at bottom */}
                    <div className="absolute inset-2 flex flex-col justify-between pointer-events-none">
                      <div className="border-b border-dashed border-[#E63946]/40"><span className="text-[6px] text-[#E63946] bg-[hsl(var(--surface-elevated))] px-0.5">&gt;30%</span></div>
                      <div className="border-b border-dashed border-[#F59E0B]/40"><span className="text-[6px] text-[#F59E0B] bg-[hsl(var(--surface-elevated))] px-0.5">≤30%</span></div>
                      <div className="border-b border-dashed border-[#3B82F6]/40"><span className="text-[6px] text-[#3B82F6] bg-[hsl(var(--surface-elevated))] px-0.5">≤15%</span></div>
                      <div className="border-b border-dashed border-[#10B981]/40"><span className="text-[6px] text-[#10B981] bg-[hsl(var(--surface-elevated))] px-0.5">≤5%</span></div>
                      <div className="border-b border-solid border-[#6B7280]/60"><span className="text-[6px] text-[#6B7280] bg-[hsl(var(--surface-elevated))] px-0.5">0</span></div>
                    </div>
                    {/* Bar */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-6 h-[calc(100%-16px)] flex items-end">
                      <div className="w-full rounded-t transition-all" style={{ height: `${Math.max(heightPercent, 3)}%`, backgroundColor: barColor, minHeight: '4px' }} />
                    </div>
                  </div>
                  <p className="text-center text-sm font-mono font-bold mt-2" style={{ color: barColor }}>{item.value}</p>
                </div>
              );
            })}

            {/* Arrow */}
            <div className="flex items-center"><ChevronRight size={24} className="text-[#6B7280]" /></div>

            {/* CLOSED Box - Higher is better */}
            <div className="w-28 flex flex-col p-3 bg-[#10B981]/5 border border-[#10B981]/20 rounded-xl">
              <p className="text-center text-sm font-semibold text-[#10B981] mb-2">CLOSED</p>
              <div className="flex-1 relative bg-[hsl(var(--surface-elevated))] rounded-lg">
                {/* Indicator lines with 0 at bottom */}
                <div className="absolute inset-2 flex flex-col justify-between pointer-events-none">
                  <div className="border-b border-dashed border-[#10B981]/60"><span className="text-[7px] text-[#10B981] bg-[hsl(var(--surface-elevated))] px-1">≥40%</span></div>
                  <div className="border-b border-dashed border-[#3B82F6]/60"><span className="text-[7px] text-[#3B82F6] bg-[hsl(var(--surface-elevated))] px-1">≥25%</span></div>
                  <div className="border-b border-dashed border-[#F59E0B]/60"><span className="text-[7px] text-[#F59E0B] bg-[hsl(var(--surface-elevated))] px-1">≥15%</span></div>
                  <div className="border-b border-dashed border-[#E63946]/60"><span className="text-[7px] text-[#E63946] bg-[hsl(var(--surface-elevated))] px-1">&lt;15%</span></div>
                  <div className="border-b border-solid border-[#6B7280]/60"><span className="text-[7px] text-[#6B7280] bg-[hsl(var(--surface-elevated))] px-1">0</span></div>
                </div>
                {/* Bar - color based on closing rate zone */}
                {(() => {
                  const closingRate = pipelineStages.leads > 0 ? (pipelineStages.closed / pipelineStages.leads) * 100 : 0;
                  const heightPercent = Math.min((closingRate / 50) * 100, 95);
                  // Color based on closing rate: ≥40% Excellent, ≥25% Good, ≥15% Average, <15% Bad
                  const getColor = () => {
                    if (closingRate >= 40) return '#10B981'; // Excellent
                    if (closingRate >= 25) return '#3B82F6'; // Good
                    if (closingRate >= 15) return '#F59E0B'; // Average
                    return '#E63946'; // Bad
                  };
                  const barColor = getColor();
                  return (
                    <>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8 h-[calc(100%-16px)] flex items-end">
                        <div className="w-full rounded-t transition-all" style={{ height: `${Math.max(heightPercent, 5)}%`, backgroundColor: barColor }} />
                      </div>
                      <p className="absolute bottom-[-24px] left-0 right-0 text-center text-lg font-mono font-bold" style={{ color: barColor }}>{pipelineStages.closed}</p>
                      <p className="absolute bottom-[-38px] left-0 right-0 text-center text-xs text-[#6B7280]">
                        {pipelineStages.leads > 0 ? `${closingRate.toFixed(1)}%` : '0%'}
                      </p>
                    </>
                  );
                })()}
              </div>
              <div className="h-10"></div>
            </div>

            {/* Arrow */}
            <div className="flex items-center"><ChevronRight size={24} className="text-[#6B7280]" /></div>

            {/* BALANCE Box - Lower is better */}
            <div className="w-28 flex flex-col p-3 bg-[#E63946]/5 border border-[#E63946]/20 rounded-xl">
              <p className="text-center text-sm font-semibold text-[#E63946] mb-2">BALANCE</p>
              <div className="flex-1 relative bg-[hsl(var(--surface-elevated))] rounded-lg">
                {/* Indicator lines with 0 at bottom */}
                <div className="absolute inset-2 flex flex-col justify-between pointer-events-none">
                  <div className="border-b border-dashed border-[#E63946]/60"><span className="text-[7px] text-[#E63946] bg-[hsl(var(--surface-elevated))] px-1">&gt;50%</span></div>
                  <div className="border-b border-dashed border-[#F59E0B]/60"><span className="text-[7px] text-[#F59E0B] bg-[hsl(var(--surface-elevated))] px-1">≤50%</span></div>
                  <div className="border-b border-dashed border-[#3B82F6]/60"><span className="text-[7px] text-[#3B82F6] bg-[hsl(var(--surface-elevated))] px-1">≤30%</span></div>
                  <div className="border-b border-dashed border-[#10B981]/60"><span className="text-[7px] text-[#10B981] bg-[hsl(var(--surface-elevated))] px-1">≤15%</span></div>
                  <div className="border-b border-solid border-[#6B7280]/60"><span className="text-[7px] text-[#6B7280] bg-[hsl(var(--surface-elevated))] px-1">0</span></div>
                </div>
                {/* Bar - color based on balance % zone */}
                {(() => {
                  const balancePercent = pipelineStages.leads > 0 ? (pipelineStages.balanced / pipelineStages.leads) * 100 : 0;
                  const heightPercent = Math.min((balancePercent / 70) * 100, 95);
                  // Color: Lower is better - ≤15% Excellent, ≤30% Good, ≤50% Average, >50% Bad
                  const getColor = () => {
                    if (balancePercent <= 15) return '#10B981'; // Excellent
                    if (balancePercent <= 30) return '#3B82F6'; // Good
                    if (balancePercent <= 50) return '#F59E0B'; // Average
                    return '#E63946'; // Bad
                  };
                  const barColor = getColor();
                  return (
                    <>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8 h-[calc(100%-16px)] flex items-end">
                        <div className="w-full rounded-t transition-all" style={{ height: `${Math.max(heightPercent, 5)}%`, backgroundColor: barColor }} />
                      </div>
                      <p className="absolute bottom-[-24px] left-0 right-0 text-center text-lg font-mono font-bold" style={{ color: barColor }}>{pipelineStages.balanced}</p>
                      <p className="absolute bottom-[-38px] left-0 right-0 text-center text-xs text-[#6B7280]">
                        {pipelineStages.leads > 0 ? `${balancePercent.toFixed(1)}%` : '0%'}
                      </p>
                    </>
                  );
                })()}
              </div>
              <div className="h-10"></div>
            </div>

          </div>
        </div>
      </SectionCard>

      {/* Row 2: Pipeline Stuck Rate + Daily Lead Flow */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="col-span-1">
          <SectionCard>
            <SectionHeader icon={Percent} title="Pipeline Stuck Rate" subtitle="% Stuck at Each Stage (Lower = Better)" />
            <div className="p-4">
              {(() => {
                // Calculate stuck rate at each pipeline stage
                // LOGIC: Lower % stuck is better (except Closing Rate - higher is better)
                const leads = pipelineStages.leads || 0;
                const greeting = pipelineStages.greeting || 0;
                const tick = pipelineStages.tick || 0;
                const registration = pipelineStages.registration || 0;
                const broker = pipelineStages.broker || 0;
                const offer = pipelineStages.offer || 0;
                const closed = pipelineStages.closed || 0;
                
                // Stuck rate = (stuck_at_stage / leads) * 100
                // For pipeline stages: LOWER is BETTER (less people stuck)
                // Thresholds: excellent ≤5%, good ≤15%, average ≤30%, poor >30%
                const conversionRates = [
                  { name: 'Greeting Stuck', rate: leads > 0 ? ((greeting / leads) * 100) : 0, isStuck: true },
                  { name: '1 Tick Stuck', rate: leads > 0 ? ((tick / leads) * 100) : 0, isStuck: true },
                  { name: 'Registration Stuck', rate: leads > 0 ? ((registration / leads) * 100) : 0, isStuck: true },
                  { name: 'Broker Stuck', rate: leads > 0 ? ((broker / leads) * 100) : 0, isStuck: true },
                  { name: 'Offer Stuck', rate: leads > 0 ? ((offer / leads) * 100) : 0, isStuck: true },
                  { name: 'Closing Rate', rate: leads > 0 ? ((closed / leads) * 100) : 0, isStuck: false }, // Higher is better
                ];
                
                // Color function - REVERSED for stuck rates (lower is better)
                const getStuckColor = (rate) => {
                  if (rate <= 5) return '#10B981';  // Excellent - very few stuck
                  if (rate <= 15) return '#3B82F6'; // Good
                  if (rate <= 30) return '#F59E0B'; // Average
                  return '#E63946'; // Poor - too many stuck
                };
                
                // For Closing Rate - higher is better (normal logic)
                const getClosingColor = (rate) => {
                  if (rate >= 40) return '#10B981';
                  if (rate >= 25) return '#3B82F6';
                  if (rate >= 15) return '#F59E0B';
                  return '#E63946';
                };
                
                return (
                  <div className="space-y-3">
                    {/* Legend */}
                    <div className="flex items-center justify-between text-[9px] text-[#9CA3AF] pb-2 border-b border-[hsl(var(--border))]">
                      <span>Pipeline: Lower % = Better</span>
                      <span>Closing: Higher % = Better</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {conversionRates.map((conv, idx) => {
                        const hasData = conv.rate > 0 || leads > 0;
                        const color = hasData 
                          ? (conv.isStuck ? getStuckColor(conv.rate) : getClosingColor(conv.rate))
                          : '#6B7280';
                        
                        // Status label based on logic
                        const getStatus = () => {
                          if (!hasData) return '';
                          if (conv.isStuck) {
                            if (conv.rate <= 5) return 'Excellent';
                            if (conv.rate <= 15) return 'Good';
                            if (conv.rate <= 30) return 'Average';
                            return 'Poor';
                          } else {
                            if (conv.rate >= 40) return 'Excellent';
                            if (conv.rate >= 25) return 'Good';
                            if (conv.rate >= 15) return 'Average';
                            return 'Poor';
                          }
                        };
                        
                        return (
                          <div 
                            key={idx} 
                            className="p-2.5 rounded-lg border"
                            style={{ backgroundColor: `${color}10`, borderColor: `${color}30` }}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-[10px] font-medium text-[#9CA3AF] truncate">{conv.name}</p>
                              <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${color}20`, color }}>{getStatus()}</span>
                            </div>
                            <p className="text-sm sm:text-lg font-mono font-bold truncate" style={{ color }}>
                              {hasData ? `${conv.rate.toFixed(1)}%` : '-'}
                            </p>
                            <div className="mt-1 h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(conv.rate, 100)}%`, backgroundColor: color }} />
                            </div>
                            {conv.isStuck && (
                              <p className="text-[8px] text-[#6B7280] mt-1">{conv.rate <= 15 ? '✓ Healthy' : '⚠ Review'}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </SectionCard>
        </div>

        {/* Daily Lead Flow - Now in Row 2 */}
        <div className="col-span-2">
          <SectionCard>
            <SectionHeader icon={Activity} title="Daily Lead Flow" subtitle="Quick Diagnosis - Where Leads Get Stuck" />
        <div className="p-6">
          {dailyLeadFlow.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyLeadFlow} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="#9CA3AF" />
                <YAxis dataKey="date" type="category" stroke="#9CA3AF" width={50} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Bar dataKey="greeting" stackId="a" fill="#E63946" name="Greeting" />
                <Bar dataKey="tick" stackId="a" fill="#F97316" name="1 Tick" />
                <Bar dataKey="registration" stackId="a" fill="#F59E0B" name="Registration" />
                <Bar dataKey="broker" stackId="a" fill="#22D3EE" name="Broker" />
                <Bar dataKey="offer" stackId="a" fill="#3B82F6" name="Offer" />
                <Bar dataKey="closed" stackId="a" fill="#10B981" name="Closed" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center">
              <div className="text-center">
                <BarChart3 size={48} className="text-[#6B7280] mx-auto mb-3" />
                <p className="text-[#9CA3AF]">No data available for selected period</p>
              </div>
            </div>
          )}
          <p className="text-xs text-[#9CA3AF] text-center mt-4">📊 Longer bars = more leads at that stage. Focus on stages with biggest drop-off.</p>
        </div>
          </SectionCard>
        </div>
      </div>

      {/* TREND GRAPH: SALES PERFORMANCE */}
      <SectionCard>
        <SectionHeader icon={TrendingUp} title="Trend Graph: Sales Performance" subtitle="Daily sales cost-per-metric trends" />
        <div className="p-4 sm:p-6">
          <TrendGraphSales dailyData={dailyData} dateFrom={dateFrom} dateTo={dateTo} usdToMyr={usdToMyr} />
        </div>
      </SectionCard>

      {/* Row 3: Sales Performance */}
      <SectionCard>
        <SectionHeader icon={TrendingUp} title="Sales Performance" subtitle="Key Sales Metrics" action={<KpiHelpButton title="Sales Performance KPI Guide" department="sales" />} />
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {salesMetrics.map((metric, idx) => {
              const color = metric.color || (metric.hasData ? getPerformanceColor(metric.rate || 0, { excellent: 90, good: 70, average: 40 }) : '#6B7280');
              const status = metric.status || (metric.hasData ? getPerformanceStatus(metric.rate || 0, { excellent: 90, good: 70, average: 40 }) : 'No Data');
              
              return (
                <div 
                  key={idx} 
                  className="p-4 rounded-lg border text-center group relative" 
                  style={{ backgroundColor: `${color}10`, borderColor: `${color}30` }}
                >
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-2">{metric.label}</p>
                  <p className="text-xl font-mono font-bold" style={{ color }}>{metric.value}</p>
                  <p className="text-xs mt-1 capitalize" style={{ color }}>{status}</p>
                  {/* Formula tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1F2937] text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                    <span className="text-[#9CA3AF]">Formula:</span> {metric.formula}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1F2937]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* PERFORMANCE TRACKER - Sales (Using Reusable Component) */}
      {hasRealData ? (
        <PerformanceTracker 
          metrics={[
            { key: 'leads', label: 'Total Leads', current: Math.round(historicalData.total_leads || 0), target: kpiTargets.sales?.registration || 0, dailyTarget: Math.round((kpiTargets.sales?.registration || 0) / daysInMonth), icon: Target, color: '#3B82F6' },
            { key: 'closed', label: 'Closed', current: Math.round(historicalData.total_closing || 0), target: kpiTargets.sales?.broker || 0, dailyTarget: Math.round((kpiTargets.sales?.broker || 0) / daysInMonth), icon: Zap, color: '#10B981' },
            { key: 'deposit', label: 'Deposit', current: Math.round(historicalData.total_deposit || 0), target: kpiTargets.sales?.priceOffer || 0, dailyTarget: Math.round((kpiTargets.sales?.priceOffer || 0) / daysInMonth), icon: DollarSign, color: '#F59E0B', prefix: '$' },
            { key: 'closingRate', label: 'Closing Rate', current: historicalData.total_leads > 0 ? parseFloat(((historicalData.total_closing / historicalData.total_leads) * 100).toFixed(1)) : 0, target: kpiTargets.sales?.followUpRate || 0, dailyTarget: Math.round((kpiTargets.sales?.followUpRate || 0) / daysInMonth), icon: TrendingUp, color: '#E63946', suffix: '%' },
            { key: 'commission', label: 'Commission', current: Math.round(historicalData.total_commission || 0), target: kpiTargets.marketing?.cpl ? kpiTargets.marketing.cpl * 1000 : 0, dailyTarget: Math.round((kpiTargets.marketing?.cpl ? kpiTargets.marketing.cpl * 1000 : 0) / daysInMonth), icon: DollarSign, color: '#F97316', prefix: 'RM ' },
            { key: 'activeClient', label: 'Active Client', current: Math.round(historicalData.total_active_client || 0), target: kpiTargets.operations?.retention || 0, dailyTarget: Math.round((kpiTargets.operations?.retention || 0) / daysInMonth), icon: UserCheck, color: '#8B5CF6' },
          ]}
          title="Performance Tracker"
          subtitle={`Sales Data • ${dateRange?.replace('2025-', '').toUpperCase() || 'MTD'}`}
          department="sales"
          realDailyData={realDailyData}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      ) : (
        <SectionCard>
          <SectionHeader icon={BarChart3} title="Performance Tracker" subtitle="Sales Monthly Target Progress" />
          <div className="p-6 text-center text-[#6B7280]">
            <p className="text-sm">Tiada data untuk tempoh ini. Pilih tarikh 2025 untuk melihat data sebenar.</p>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// =====================================================
// OPERATIONS TAB
// =====================================================
function OperationsTab({ ibId, dateRange, dateFrom, dateTo, dailyData, adjustment, setAdjustment, scenario, setScenario, forecastPeriod, setForecastPeriod, historicalData, isLoading, isFiraInd, usdToMyr = 4.0, kpiTargets = {}, kpiThresholds = null }) {
  // Check if we have real data (from localStorage OR API)
  const hasRealData = historicalData && Object.keys(historicalData).length > 0;
  
  // Generate realDailyData for PerformanceTracker
  const realDailyData = useMemo(() => {
    if (!isFiraInd || !dailyData?.sales?.length) return null;
    
    // Filter by date range
    const filtered = dailyData.sales.filter(d => {
      const itemDate = new Date(d.date);
      return itemDate >= dateFrom && itemDate <= dateTo;
    });
    
    if (!filtered.length) return null;
    
    // Helper function to sum broker values
    const sumBrokers = (d, field) => {
      if (d.brokers) {
        return Object.values(d.brokers).reduce((sum, b) => sum + (b?.[field] || 0), 0);
      }
      return (d.brokerA?.[field] || 0) + (d.brokerB?.[field] || 0) + (d.brokerC?.[field] || 0);
    };
    
    // Operations uses sales data for brokers
    return filtered.map(d => ({
      date: d.date,
      activeClient: sumBrokers(d, 'active'),
      tradedLot: sumBrokers(d, 'lots'),
      commission: d.total_commission_usd !== undefined ? d.total_commission_usd : sumBrokers(d, 'commission_usd') || sumBrokers(d, 'commission'),
      subscribers: (d.subsTG || 0) + (d.subsBot || 0),
      leads: (d.leadsM?.new || 0) + (d.leadsM?.prospect || 0) + (d.leadsM?.vip || 0) +
             (d.leadsS?.new || 0) + (d.leadsS?.prospect || 0) + (d.leadsS?.vip || 0) +
             (d.leadsIB?.new || 0) + (d.leadsIB?.prospect || 0) + (d.leadsIB?.vip || 0),
      closed: d.closed || 0,
    }));
  }, [isFiraInd, dailyData, dateFrom, dateTo]);
  
  const getMultiplier = () => {
    const multipliers = { 'today': 0.05, 'yesterday': 0.04, 'this-week': 0.3, 'last-week': 0.25, 'this-month': 1, 'mtd': 1, 'last-month': 0.9, 'full': 3 };
    return multipliers[dateRange] || 1;
  };
  const m = getMultiplier();

  // Aggregate leadsIB from dailyData (sales tab data)
  const ibLeadsStats = (() => {
    // Use real data from dailyData.sales for ALL IBs (not just FIRA IND)
    if (dailyData?.sales?.length > 0) {
      const filteredSales = dailyData.sales.filter(d => {
        const itemDate = new Date(d.date);
        return itemDate >= dateFrom && itemDate <= dateTo;
      });
      let leadsIB_new = 0, leadsIB_prospect = 0, leadsIB_vip = 0;
      
      filteredSales.forEach(d => {
        const leadsIB = d.leadsIB || {};
        leadsIB_new += (leadsIB.new || 0);
        leadsIB_prospect += (leadsIB.prospect || 0);
        leadsIB_vip += (leadsIB.vip || 0);
      });
      return { 
        leadsIB_new,
        leadsIB_prospect,
        leadsIB_vip,
        leadsIB_total: leadsIB_new + leadsIB_prospect + leadsIB_vip
      };
    }
    return { 
      leadsIB_new: 0,
      leadsIB_prospect: 0,
      leadsIB_vip: 0,
      leadsIB_total: historicalData?.total_leads_ib || 0
    };
  })();

  // Scorecard metrics for Operations - ALWAYS show New/Prospect/VIP Leads (IB)
  const scorecardMetrics = hasRealData ? [
    { label: 'Subscribers', value: Math.round(historicalData.total_subscribers || 0).toLocaleString(), icon: Users, color: '#3B82F6' },
    { label: 'Closed', value: Math.round(historicalData.total_closing || 0).toLocaleString(), icon: Zap, color: '#10B981' },
    { label: 'Active Client', value: Math.round(historicalData.total_active_client || 0).toLocaleString(), icon: UserCheck, color: '#E63946' },
    { label: 'Traded Lot', value: Math.round(historicalData.total_traded_lot || 0).toLocaleString(), icon: BarChart3, color: '#F59E0B' },
    { label: 'Commission (RM)', value: `RM ${Math.round(historicalData.total_commission || 0).toLocaleString()}`, icon: DollarSign, color: '#10B981' },
  ] : isFiraInd ? [
    { label: 'Subscribers', value: '-', icon: Users, color: '#6B7280' },
    { label: 'Closed', value: '-', icon: Zap, color: '#6B7280' },
    { label: 'Active Client', value: '-', icon: UserCheck, color: '#6B7280' },
    { label: 'Traded Lot', value: '-', icon: BarChart3, color: '#6B7280' },
    { label: 'Commission (RM)', value: '-', icon: DollarSign, color: '#6B7280' },
  ] : [
    { label: 'Subscribers', value: Math.round(100 * m).toLocaleString(), icon: Users, color: '#3B82F6' },
    { label: 'Closed', value: Math.round(28 * m).toLocaleString(), icon: Zap, color: '#10B981' },
    { label: 'Active Client', value: Math.round(45 * m).toLocaleString(), icon: UserCheck, color: '#E63946' },
    { label: 'Traded Lot', value: (98.2 * m).toFixed(1), icon: BarChart3, color: '#F59E0B' },
    { label: 'Commission (RM)', value: `RM ${Math.round(6200 * m * usdToMyr).toLocaleString()}`, icon: DollarSign, color: '#10B981' },
  ];

  // Operations Funnel - NEW: Subs → Post Reach → Reaction/Poll → Leads → Closed
  // Data sources: Telegram API (subs), MTProto (avg views, reactions), Daily Data IB (leads, closed)
  const operationsFunnel = useMemo(() => {
    // Debug log
    console.log('OperationsFunnel - historicalData:', historicalData);
    
    // Use telegram_subscribers for channel subscribers (from MTProto stats)
    // This data is available even without Google Sheet
    const subs = historicalData?.telegram_subscribers || 0;
    // Post Reach = avg views per post (from MTProto cache)
    const avgViews = Math.round(historicalData?.avg_views_per_post || 0);
    // Reactions = avg reactions per post (from MTProto cache)
    const avgReactions = Math.round(historicalData?.avg_reactions_per_post || 0);
    // Leads (IB) and Closed from Daily Data
    const leads = Math.round(historicalData?.total_leads_ib || 0);
    const closed = Math.round(historicalData?.total_closing || 0);
    
    console.log('OperationsFunnel - values:', { subs, avgViews, avgReactions, leads, closed });
    
    // Calculate conversion rates
    const reachConv = subs > 0 ? ((avgViews / subs) * 100) : 0;
    const engagementConv = avgViews > 0 ? ((avgReactions / avgViews) * 100) : 0;
    const leadConv = avgReactions > 0 ? ((leads / avgReactions) * 100) : 0;
    const closeConv = leads > 0 ? ((closed / leads) * 100) : 0;
    
    return [
      { stage: 'Subscribers', value: subs, convRate: reachConv, nextStage: 'Post Reach' },
      { stage: 'Post Reach', value: avgViews, convRate: engagementConv, nextStage: 'Reactions' },
      { stage: 'Reaction/Poll', value: avgReactions, convRate: leadConv, nextStage: 'Leads' },
      { stage: 'Leads', value: leads, convRate: closeConv, nextStage: 'Closed' },
      { stage: 'Closed', value: closed, convRate: null, nextStage: null },
    ];
  }, [hasRealData, historicalData, isFiraInd, m]);
  
  // Conversion Rates Details - similar to Marketing tab
  const conversionRates = useMemo(() => {
    const subs = operationsFunnel[0].value;
    const reach = operationsFunnel[1].value;
    const reactions = operationsFunnel[2].value;
    const leads = operationsFunnel[3].value;
    const closed = operationsFunnel[4].value;
    
    return [
      { 
        label: 'Reach Conversion', 
        desc: 'Subscribers → Post Reach',
        value: subs > 0 ? ((reach / subs) * 100).toFixed(1) : 0,
        color: '#3B82F6',
        icon: Eye
      },
      { 
        label: 'Engagement Conversion', 
        desc: 'Post Reach → Reactions',
        value: reach > 0 ? ((reactions / reach) * 100).toFixed(2) : 0,
        color: '#F59E0B',
        icon: MessageCircle
      },
      { 
        label: 'Lead Conversion', 
        desc: 'Reactions → Leads',
        value: reactions > 0 ? ((leads / reactions) * 100).toFixed(1) : 0,
        color: '#10B981',
        icon: Target
      },
      { 
        label: 'Closing Rate', 
        desc: 'Leads → Closed',
        value: leads > 0 ? ((closed / leads) * 100).toFixed(1) : 0,
        color: '#8B5CF6',
        icon: CheckCircle
      },
    ];
  }, [operationsFunnel]);

  // Activity metrics - REAL DATA from various sources
  // Live Sessions: from channel activity with labels "live session", "livetrade", "webinar"
  // Signals Sent: from Daily Data Operation tab - total signals
  // Content Posts: from Channel Analytic - split for public and vip
  const activityMetrics = useMemo(() => {
    // Count live sessions from dailyData marketing activities
    let liveSessions = 0;
    let contentPostsPublic = 0;
    let contentPostsVip = 0;

    if (dailyData && dailyData.length > 0) {
      dailyData.forEach(day => {
        // Count activities with live session related labels
        if (day.activity && Array.isArray(day.activity)) {
          day.activity.forEach(act => {
            const label = (act.label || '').toLowerCase();
            if (label.includes('live') || label.includes('webinar') || label.includes('livetrade')) {
              liveSessions += 1;
            }
          });
        }
      });
    }

    // Signals & content posts: pull ONLY from real historicalData; no per-channel dummies.
    const signalsSent = hasRealData ? Number(historicalData.total_signals || 0) : 0;
    contentPostsPublic = hasRealData ? Number(historicalData.content_posts_public || 0) : 0;
    contentPostsVip = hasRealData ? Number(historicalData.content_posts_vip || 0) : 0;

    return [
      { label: 'Live Sessions',        value: liveSessions,       icon: Video,  color: '#E63946', hasData: liveSessions > 0 },
      { label: 'Signals Sent',         value: signalsSent,        icon: Signal, color: '#F59E0B', hasData: signalsSent > 0 },
      { label: 'Content Posts (Public)', value: contentPostsPublic, icon: Radio, color: '#3B82F6', hasData: contentPostsPublic > 0 },
      { label: 'Content Posts (VIP)',    value: contentPostsVip,    icon: Radio, color: '#8B5CF6', hasData: contentPostsVip > 0 },
    ];
  }, [dailyData, hasRealData, historicalData]);

  // Operation Performance - with engagement rates for public and vip channels.
  // Uses ONLY real data from historicalData. If no data, displays "-".
  // No hardcoded mock fallbacks — every channel must show its own real metrics.
  const operationPerformance = useMemo(() => {
    const engagementPublic = Number(historicalData?.engagement_rate_public) || 0;
    const engagementVip = Number(historicalData?.engagement_rate_vip) || 0;
    const signalWinRate = Number(historicalData?.signal_win_rate) || 0;
    const avgLotPerClientNum = (historicalData && Number(historicalData.total_active_client) > 0)
      ? Number(historicalData.total_traded_lot) / Number(historicalData.total_active_client)
      : 0;
    const commPerClientNum = (historicalData && Number(historicalData.total_active_client) > 0)
      ? Number(historicalData.total_commission) / Number(historicalData.total_active_client)
      : 0;

    // Threshold-driven color + status helper
    const ops = kpiThresholds?.operation || {};
    const evalKpi = (val, t, lowerBetter = false) => {
      if (!t || val === null || val === undefined || isNaN(val)) return { color: '#6B7280', status: 'No Data' };
      const exc = Number(t.excellent), good = Number(t.good), avg = Number(t.average);
      if (lowerBetter) {
        if (val <= exc) return { color: '#10B981', status: 'Excellent' };
        if (val <= good) return { color: '#3B82F6', status: 'Good' };
        if (val <= avg) return { color: '#F59E0B', status: 'Average' };
        return { color: '#E63946', status: 'Poor' };
      }
      if (val >= exc) return { color: '#10B981', status: 'Excellent' };
      if (val >= good) return { color: '#3B82F6', status: 'Good' };
      if (val >= avg) return { color: '#F59E0B', status: 'Average' };
      return { color: '#E63946', status: 'Poor' };
    };

    const erPub = ops.ER_PUBLIC || { excellent: 5, good: 3, average: 1 };
    const erVip = ops.ER_VIP || { excellent: 15, good: 8, average: 3 };
    const swr = ops.SIGNAL_WIN_RATE || { excellent: 80, good: 65, average: 50 };
    const alc = ops.AVG_LOT_CLIENT || { excellent: 5, good: 2, average: 0.5 };
    const cc = ops.COMM_CLIENT || { excellent: 500, good: 200, average: 50 };

    const pubEval = engagementPublic > 0 ? evalKpi(engagementPublic, erPub) : { color: '#6B7280', status: 'No Data' };
    const vipEval = engagementVip > 0 ? evalKpi(engagementVip, erVip) : { color: '#6B7280', status: 'No Data' };
    const swrEval = signalWinRate > 0 ? evalKpi(signalWinRate, swr) : { color: '#6B7280', status: 'No Data' };
    const alcEval = avgLotPerClientNum > 0 ? evalKpi(avgLotPerClientNum, alc) : { color: '#6B7280', status: 'No Data' };
    const ccEval = commPerClientNum > 0 ? evalKpi(commPerClientNum, cc) : { color: '#6B7280', status: 'No Data' };

    return [
      { label: 'Engagement (Public)', value: engagementPublic > 0 ? `${engagementPublic.toFixed(2)}%` : '-', color: pubEval.color, status: pubEval.status },
      { label: 'Engagement (VIP)', value: engagementVip > 0 ? `${engagementVip.toFixed(2)}%` : '-', color: vipEval.color, status: vipEval.status },
      { label: 'Signal Win Rate', value: signalWinRate > 0 ? `${signalWinRate}%` : '-', color: swrEval.color, status: swrEval.status },
      { label: 'Avg Lot/Client', value: avgLotPerClientNum > 0 ? avgLotPerClientNum.toFixed(2) : '-', color: alcEval.color, status: alcEval.status },
      { label: 'Commission/Client', value: commPerClientNum > 0 ? `RM ${commPerClientNum.toFixed(2)}` : '-', color: ccEval.color, status: ccEval.status },
    ];
  }, [hasRealData, historicalData, isFiraInd, kpiThresholds]);

  // Weekly activity trend — derived from real dailyData (activities & signals)
  const weeklyTrend = useMemo(() => {
    const buckets = { W1: { live: 0, signals: 0, content: 0 }, W2: { live: 0, signals: 0, content: 0 }, W3: { live: 0, signals: 0, content: 0 }, W4: { live: 0, signals: 0, content: 0 } };
    if (dailyData && dailyData.length > 0) {
      dailyData.forEach((day) => {
        const dt = new Date(day.date || day.date_str || 0);
        if (!dt || isNaN(dt.getTime())) return;
        const dom = dt.getDate();
        const wk = dom <= 7 ? 'W1' : dom <= 14 ? 'W2' : dom <= 21 ? 'W3' : 'W4';
        if (Array.isArray(day.activity)) {
          day.activity.forEach((act) => {
            const label = (act.label || '').toLowerCase();
            if (label.includes('live') || label.includes('webinar') || label.includes('livetrade')) buckets[wk].live += 1;
            else if (label.includes('signal')) buckets[wk].signals += 1;
            else buckets[wk].content += 1;
          });
        }
      });
    }
    return ['W1', 'W2', 'W3', 'W4'].map((k) => ({ week: k, ...buckets[k] }));
  }, [dailyData]);

  const hasWeeklyActivity = weeklyTrend.some((w) => (w.live + w.signals + w.content) > 0);

  // Conversion status helper (same as Marketing tab)
  const getConversionStatus = (rate) => {
    if (rate >= 50) return { status: 'excellent', color: '#3B82F6' };
    if (rate >= 30) return { status: 'good', color: '#10B981' };
    if (rate >= 15) return { status: 'average', color: '#F59E0B' };
    return { status: 'poor', color: '#E63946' };
  };

  // Performance Tracker Data for Operations
  const currentDate = new Date();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

  return (
    <div className="space-y-6">
      <SignalProviderPerformance ibId={ibId} dateFrom={dateFrom} dateTo={dateTo} />
      {/* PERFORMANCE PROJECTIONS */}
      <DepartmentProjections 
        department="operations" 
        adjustment={adjustment} 
        setAdjustment={setAdjustment}
        scenario={scenario}
        setScenario={setScenario}
        forecastPeriod={forecastPeriod}
        setForecastPeriod={setForecastPeriod}
        historicalData={historicalData}
        isFiraInd={isFiraInd}
      />
      {/* SCORECARD (2 rows for 9 metrics - 5 per row) */}
      <SectionCard>
        <SectionHeader icon={BarChart3} title="Operations Scorecard" subtitle="Key Department Metrics" />
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {scorecardMetrics.map((metric, idx) => {
              const Icon = metric.icon;
              return (
                <div key={idx} className="p-4 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-center">
                  <Icon size={20} style={{ color: metric.color }} className="mx-auto mb-2" />
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">{metric.label}</p>
                  <p className="text-xl font-mono font-bold text-white">{metric.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* Row 1: Operations Funnel + Conversion Rates Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Operations Funnel - Pyramid */}
        <SectionCard>
          <SectionHeader icon={Settings} title="Operations Funnel" subtitle="Subscriber to Client Journey" />
          <div className="p-6">
            <div className="space-y-2">
              {operationsFunnel.map((item, idx) => {
                const widthPercent = 100 - (idx * 16);
                const convRate = item.convRate;
                const color = convRate === null ? '#10B981' : getPerformanceColor(convRate, { good: 50, average: 20 });
                const nextItem = operationsFunnel[idx + 1];
                
                return (
                  <div key={item.stage} className="flex flex-col items-center">
                    <div 
                      className="relative h-12 rounded-lg flex items-center justify-between px-4 transition-all"
                      style={{ width: `${widthPercent}%`, backgroundColor: item.value > 0 ? color : '#374151', minWidth: '140px' }}
                    >
                      <span className="text-white font-medium text-sm">{item.stage}</span>
                      <span className="text-white font-mono font-bold">{item.value > 0 ? item.value.toLocaleString() : '0'}</span>
                    </div>
                    {convRate !== null && nextItem && (
                      <div className="flex items-center gap-1 my-1">
                        <ArrowDown size={12} className="text-[#9CA3AF]" />
                        <span className="text-xs font-medium" style={{ color: item.value > 0 ? getPerformanceColor(convRate, { good: 50, average: 20 }) : '#6B7280' }}>
                          {convRate > 0 ? `${convRate.toFixed(1)}%` : '0%'} → {nextItem.stage}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>

        {/* Conversion Rates Details - SAME STYLE AS MARKETING TAB */}
        <SectionCard>
          <SectionHeader icon={BarChart3} title="Conversion Rates Detail" subtitle="Stage-by-Stage Analysis" action={<KpiHelpButton title="Operations Conversion KPI Guide" customKpis={OPS_CONVERSION_KPIS} />} />
          <div className="p-6 space-y-3">
            {conversionRates.map((rate, idx) => {
              const numValue = parseFloat(rate.value) || 0;
              const { status, color } = getConversionStatus(numValue);
              const showNoData = numValue === 0;
              
              return (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-4 rounded-lg border"
                  style={{ backgroundColor: showNoData ? 'rgba(107, 114, 128, 0.1)' : `${color}10`, borderColor: showNoData ? 'rgba(107, 114, 128, 0.3)' : `${color}30` }}
                >
                  <div>
                    <p className="text-sm font-medium text-white">{rate.label}</p>
                    <p className="text-xs text-[#9CA3AF]">{rate.desc}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {showNoData ? (
                      <p className="text-xl font-mono font-bold text-[#6B7280]">-</p>
                    ) : (
                      <>
                        <p className="text-2xl font-mono font-bold" style={{ color }}>{rate.value}%</p>
                        <Badge style={{ backgroundColor: `${color}20`, color }} className="border-none text-[10px]">
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Row 2: Activity Metrics */}
      <SectionCard>
        <SectionHeader icon={Activity} title="Activity Metrics" subtitle="IB & Signal Provider Work" />
        <div className="p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {activityMetrics.map((metric, idx) => {
              const Icon = metric.icon;
              
              return (
                <div key={idx} className="p-4 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))] text-center">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: metric.hasData ? `${metric.color}20` : '#37415120' }}>
                    <Icon size={24} style={{ color: metric.hasData ? metric.color : '#6B7280' }} />
                  </div>
                  <p className="text-2xl font-mono font-bold mb-1" style={{ color: metric.hasData ? metric.color : '#6B7280' }}>
                    {metric.value > 0 ? metric.value.toLocaleString() : '0'}
                  </p>
                  <p className="text-xs text-[#9CA3AF]">{metric.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* TREND GRAPH: OPERATION PERFORMANCE */}
      <SectionCard>
        <SectionHeader icon={TrendingUp} title="Trend Graph: Operation Performance" subtitle="Daily operation metric trends" />
        <div className="p-4 sm:p-6">
          <TrendGraphOperation dailyData={dailyData} dateFrom={dateFrom} dateTo={dateTo} usdToMyr={usdToMyr} />
        </div>
      </SectionCard>

      {/* Row 3: Operation Performance (with Engagement Rates) */}
      <SectionCard>
        <SectionHeader icon={TrendingUp} title="Operation Performance" subtitle="Channel Engagement & Client Metrics" action={<KpiHelpButton title="Operation Performance KPI Guide" metrics={['ER_PUBLIC','ER_VIP','SIGNAL_WIN_RATE','AVG_LOT_CLIENT','COMM_CLIENT']} />} />
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {operationPerformance.map((metric, idx) => {
              const color = metric.color || '#6B7280';
              const status = metric.status || 'No Data';
              return (
                <div key={idx} className="p-4 rounded-lg border text-center" style={{ backgroundColor: `${color}10`, borderColor: `${color}30` }}>
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-2">{metric.label}</p>
                  <p className="text-xl font-mono font-bold" style={{ color: metric.value !== '-' ? color : '#6B7280' }}>{metric.value}</p>
                  <p className="text-[10px] mt-1 capitalize" style={{ color }}>{status}</p>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* Row 3: Weekly Activity Trend */}
      {hasWeeklyActivity ? (
        <SectionCard>
          <SectionHeader icon={TrendingUp} title="Weekly Activity Trend" subtitle="Live Sessions, Signals & Content" />
          <div className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={weeklyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="week" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Area type="monotone" dataKey="live" stackId="1" stroke="#E63946" fill="#E63946" fillOpacity={0.3} name="Live Sessions" />
                <Area type="monotone" dataKey="signals" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} name="Signals" />
                <Area type="monotone" dataKey="content" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} name="Content" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      ) : (
        <SectionCard>
          <SectionHeader icon={TrendingUp} title="Weekly Activity Trend" subtitle="Live Sessions, Signals & Content" />
          <div className="p-6 text-center text-[#6B7280] text-sm">
            Tiada aktiviti tercatat untuk period ini. Sila log aktiviti di Channel Activity.
          </div>
        </SectionCard>
      )}

      {/* BROKER COM/LOT BREAKDOWN */}
      {hasRealData && historicalData?.broker_breakdown?.length > 0 && (
        <SectionCard>
          <SectionHeader icon={TrendingUp} title="Commission per Lot by Broker" subtitle="Avg Com/Lot = Commission USD ÷ Traded Lot" />
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {historicalData.broker_breakdown.map(b => (
                <div key={b.name} className="bg-[hsl(var(--surface))] rounded-xl p-4 border border-[hsl(var(--border))]" data-testid={`broker-cpl-${b.name}`}>
                  <p className="text-xs font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2">{b.name}</p>
                  <p className={`text-2xl font-black ${b.com_per_lot >= 12 ? 'text-emerald-400' : b.com_per_lot >= 8 ? 'text-amber-400' : 'text-red-400'}`}>
                    ${b.com_per_lot.toFixed(2)}
                  </p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{b.lots.toFixed(1)} lots | ${b.commission_usd.toFixed(0)} USD</p>
                </div>
              ))}
              {/* Overall Avg */}
              {(() => {
                const totalLots = historicalData.broker_breakdown.reduce((s, b) => s + b.lots, 0);
                const totalComm = historicalData.broker_breakdown.reduce((s, b) => s + b.commission_usd, 0);
                const avgCpl = totalLots > 0 ? totalComm / totalLots : 0;
                return (
                  <div className="bg-[hsl(var(--surface-elevated))] rounded-xl p-4 border-2 border-dashed border-[hsl(var(--border))]" data-testid="broker-cpl-avg">
                    <p className="text-xs font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2">AVG COM/LOT</p>
                    <p className={`text-2xl font-black ${avgCpl >= 12 ? 'text-emerald-400' : avgCpl >= 8 ? 'text-amber-400' : 'text-red-400'}`}>
                      ${avgCpl.toFixed(2)}
                    </p>
                    <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{totalLots.toFixed(1)} lots | ${totalComm.toFixed(0)} USD</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </SectionCard>
      )}

      {/* PERFORMANCE TRACKER - Operations (Using Reusable Component) */}
      {hasRealData ? (
        <PerformanceTracker 
          metrics={[
            { key: 'activeClient', label: 'Active Client', current: Math.round(historicalData.total_active_client || 0), target: kpiTargets.operations?.retention || 0, dailyTarget: Math.round((kpiTargets.operations?.retention || 0) / daysInMonth), icon: UserCheck, color: '#10B981' },
            { key: 'tradedLot', label: 'Traded Lot', current: Math.round(historicalData.total_traded_lot || 0), target: kpiTargets.operations?.liveSessions || 0, dailyTarget: Math.round((kpiTargets.operations?.liveSessions || 0) / daysInMonth), icon: BarChart3, color: '#F59E0B' },
            { key: 'commission', label: 'Commission', current: Math.round(historicalData.total_commission || 0), target: kpiTargets.marketing?.cpl ? kpiTargets.marketing.cpl * 1000 : 0, dailyTarget: Math.round((kpiTargets.marketing?.cpl ? kpiTargets.marketing.cpl * 1000 : 0) / daysInMonth), icon: DollarSign, color: '#E63946', prefix: 'RM ' },
            { key: 'subscribers', label: 'Subscribers', current: Math.round(historicalData.total_subscribers || 0), target: kpiTargets.sales?.greeting || 0, dailyTarget: Math.round((kpiTargets.sales?.greeting || 0) / daysInMonth), icon: Users, color: '#3B82F6' },
            { key: 'leads', label: 'Total Leads', current: Math.round(historicalData.total_leads || 0), target: kpiTargets.sales?.registration || 0, dailyTarget: Math.round((kpiTargets.sales?.registration || 0) / daysInMonth), icon: Target, color: '#F97316' },
            { key: 'closed', label: 'Closed', current: Math.round(historicalData.total_closing || 0), target: kpiTargets.sales?.broker || 0, dailyTarget: Math.round((kpiTargets.sales?.broker || 0) / daysInMonth), icon: Zap, color: '#8B5CF6' },
          ]}
          title="Performance Tracker"
          subtitle={`Operations Data • ${dateRange?.replace('2025-', '').toUpperCase() || 'MTD'}`}
          department="operation"
          realDailyData={realDailyData}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      ) : (
        <SectionCard>
          <SectionHeader icon={BarChart3} title="Performance Tracker" subtitle="Operations Monthly Target Progress" />
          <div className="p-6 text-center text-[#6B7280]">
            <p className="text-sm">Tiada data untuk tempoh ini. Pilih tarikh 2025 untuk melihat data sebenar.</p>
          </div>
        </SectionCard>
      )}
    </div>
  );
}


export default function ChannelPerformanceContent(props) {
  return (
    <ChannelPerformanceErrorBoundary>
      <ChannelPerformanceContentInner {...props} />
    </ChannelPerformanceErrorBoundary>
  );
}
